/**
 * Client workspace · العقد tab — Task 303-A.
 *
 * Task file: `tasks/303A-agency-client-contract-v1.md` §6.
 *
 * ---------------------------------------------------------------------
 * WHAT THIS PAGE IS. The agency's contract with THIS client: a PDF the
 * two parties executed OUTSIDE this product, uploaded as evidence, with
 * the commercial snapshot that was agreed at the time.
 *
 * It is NOT the agency's own subscription to Super Agency (`/app/billing`),
 * NOT the agency's package definitions (`/app/services`), and NOT
 * Operating Setup. The copy says so at the top, because those concepts
 * share vocabulary and are routinely blurred.
 *
 * NO SIGNATURE HAPPENS HERE, AND NO COPY MAY SUGGEST ONE. The states are
 * «مسودة» / «موثّق خارج المنصة» / «مستبدَل». There is no «إرسال للتوقيع»,
 * no signature pad and no e-signature provider (§2.4).
 *
 * ONE ACKNOWLEDGEMENT CONTROL (303-B), AND IT IS NOT A SIGNATURE. It
 * records that this user reviewed the released document; the server writes
 * an append-only receipt bound to that document. Not a status, not approval
 * of the contents, not approval of future versions. It is the ONLY write on
 * the client surface, and a test pins that count.
 *
 * NO PAYMENT SURFACE. The amount is a RECORD of what was agreed, not a
 * charge: no checkout, no invoice, no provider (§2.9).
 *
 * ONE SERVER ANSWER DECIDES EVERYTHING. `get-agency-client-contracts`
 * returns `viewer: 'agency' | 'client'` plus `can_manage` / `can_download`,
 * and this page renders what it is told. It never infers authority from
 * role strings it holds locally, and the two projections are two
 * different SQL functions, so the client branch cannot receive an
 * agency-only field even in principle (§2.10).
 *
 * NOTHING IS EVER DELETED. Replacement appends a version; retirement
 * archives. There is no delete control anywhere on this page (§2.6).
 * ---------------------------------------------------------------------
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useParams } from 'react-router-dom';
import { CONTRACT_ACK_STATEMENT_AR, CONTRACT_ACK_TEXT_VERSION } from '@kmi/contracts';
import { isApiError } from '../../api/client';
import { saveBlobAsFile } from '../../api/download';
import {
  acknowledgeContractVersion,
  completeContractUpload,
  createAgencyClientContract,
  createContractUpload,
  getClientContracts,
  getMyClientContracts,
  getContractDocumentDownload,
  releaseContractVersion,
  setContractArchiveState,
} from '../../api/endpoints';
import type {
  AgencyContract,
  AgencyContractOverviewData,
  AgencyContractVersion,
  AgencyContractAcknowledgement,
  ClientContract,
  ClientContractAcknowledgement,
  ClientContractOverviewData,
  ClientContractVersion,
  ClientContractsOverviewData,
  ContractBillingInterval,
  ContractEventType,
  ContractServiceLine,
  ContractVersionBase,
  ContractVersionStatus,
} from '../../api/types';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  NotFoundState,
  PermissionDeniedState,
} from '../../components/states';

const MAX_CONTRACT_BYTES = 26_214_400;

/**
 * §2.4 — the released state names the fact (executed elsewhere) and never
 * the act (signing). «موقّع» is deliberately absent from this map.
 */
export const CONTRACT_VERSION_STATUS_LABELS_AR: Readonly<Record<ContractVersionStatus, string>> = {
  draft: 'مسودة',
  released: 'موثّق خارج المنصة',
  superseded: 'مستبدَل',
};

/**
 * The released state has exactly ONE name on this page.
 *
 * An independent UX review found three — «موثّق خارج المنصة» on the pill,
 * «نسخة معتمدة» in the empty state and «النسخة السارية» as a heading — on
 * one screen, for one state. §2.4 exists precisely to stop the released
 * state being renamed into something that sounds like an act someone
 * performed, so the vocabulary is centralised here and used at all three
 * sites.
 */
export const CONTRACT_RELEASED_NOUN_AR = 'النسخة الموثّقة خارج المنصة';

/**
 * The INDEFINITE form, for negative sentences.
 *
 * «لا توجد» takes an indefinite noun — «لا توجد النسخة الموثّقة» is not
 * grammatical Arabic. Collapsing everything onto one definite constant
 * produced two ungrammatical sentences, so the vocabulary is one *pair*:
 * definite for headings, indefinite for "there is no…". Both name the same
 * fact, and neither names an act (§2.4).
 */
export const CONTRACT_RELEASED_NOUN_INDEF_AR = 'نسخة موثّقة خارج المنصة';

/** Bidi isolate. See {@link formatContractPeriodAr}. */
const LRI = '\u2066';
const PDI = '\u2069';

/**
 * Wrap a Latin-script run (a date, a timestamp) so the bidi algorithm
 * cannot reorder its internal parts inside an RTL paragraph.
 *
 * STILL USED FOR MIXED-DIRECTION STRINGS ONLY — a phrase that is part
 * Arabic and part Latin, where the Latin runs have to be isolated
 * INDIVIDUALLY inside one string and there is no element to hang direction
 * on ({@link formatContractPeriodAr}, {@link formatContractTermAr},
 * {@link formatContractAmountAr}). A value that is a Latin run in its
 * entirety is carried by markup instead — see {@link LtrRun}, and
 * {@link stripBidiControls} for why an isolate was not enough on its own.
 */
export function isolateLtr(value: string): string {
  return `${LRI}${value}${PDI}`;
}

/**
 * Every invisible bidi control character, in one class.
 *
 * WHY THIS EXISTS, and it is the whole staging defect. CLDR's `ar` date
 * patterns embed U+200F RIGHT-TO-LEFT MARK before each separator, so
 * `Intl.DateTimeFormat('ar-SA', …).format(…)` does not return `28/08/2026`
 * — it returns `28‏/08‏/2026`. Those marks are STRONG RTL
 * characters sitting INSIDE the Latin run, and an isolate around the
 * outside cannot fix an order that is being decided on the inside: UBA rule
 * W2 reclassifies the digits after each mark from EN to AN, the separators
 * lose W4's protection, and the run renders as `282026/08/`. That is
 * exactly what a visual QA pass found on staging, on the one surface where
 * the date carries legal weight.
 *
 * So the marks are REMOVED rather than fought. What is left is
 * `EN CS EN CS EN`, which W4 folds into a single numeric run — stable in
 * logical order under an LTR base direction AND under an RTL one, with or
 * without any surrounding markup. Direction is then carried by {@link
 * LtrRun} rather than by more invisible characters.
 *
 * The class covers the marks (LRM/RLM/ALM), the isolates and the
 * deprecated embedding/override codes, so a value that arrives carrying any
 * of them — from `Intl`, or from the wire — cannot smuggle a reordering
 * into a rendered fact.
 */
const BIDI_CONTROLS =
  // U+200E LRM, U+200F RLM, U+061C ALM; U+202A–U+202E the deprecated
  // embeddings and overrides; U+2066–U+2069 the isolates. Written as
  // escapes on purpose — the literals are invisible, and an invisible
  // character in a character class is not reviewable.
  /[\u200E\u200F\u061C\u202A-\u202E\u2066-\u2069]/g;

/** Strip every bidi control character. See {@link BIDI_CONTROLS}. */
export function stripBidiControls(value: string): string {
  return value.replace(BIDI_CONTROLS, '');
}

/**
 * A value that is a Latin run in its entirety — a date, a timestamp —
 * rendered with its direction carried by MARKUP.
 *
 * `bdi` is `unicode-bidi: isolate` by default, so the run cannot be
 * reordered against the Arabic around it; the explicit `dir="ltr"` sets the
 * base direction INSIDE it. Both halves are load-bearing. `bdi` alone
 * defaults to `dir="auto"`, which picks the first STRONG character — and in
 * `28/08/2026، 1:05 م` the digits are not strong, so the first strong
 * character is the Arabic day-period marker and the whole stamp would be
 * laid out right-to-left.
 *
 * Markup rather than control characters because the characters are
 * invisible: they survive no copy/paste, no `textContent` read and no
 * assertion anyone remembers to write, and the defect this replaces was
 * invisible in the source for exactly that reason.
 */
export function LtrRun({ children }: { readonly children: ReactNode }): JSX.Element {
  return <bdi dir="ltr">{children}</bdi>;
}

export const CONTRACT_VERSION_STATUS_PILL: Readonly<Record<ContractVersionStatus, string>> = {
  draft: 'pill-draft',
  released: 'pill-active',
  superseded: 'pill-archived',
};

export const CONTRACT_BILLING_INTERVAL_LABELS_AR: Readonly<
  Record<ContractBillingInterval, string>
> = {
  one_time: 'دفعة واحدة',
  monthly: 'شهري',
  quarterly: 'ربع سنوي',
  yearly: 'سنوي',
  custom: 'حسب الاتفاق',
};

/**
 * Amount + ISO currency as one string.
 *
 * LATIN DIGITS, deliberately. Plain `ar-SA` renders Arabic-Indic numerals,
 * and an independent UX review pointed out that the same commercial figure
 * on the sibling surface — the client's own services page — is Latin. A
 * client comparing the two would see one agreed price written two ways.
 * Everything else on this card (term, version number, dates) is Latin too.
 *
 * The `catch` is defence against wire data, not against the form: the form
 * already rejects anything outside `^[A-Za-z]{3}$`, but a currency code
 * arrives here from the database and `Intl` throws on a malformed one. It
 * falls back to «<number> <CODE>» rather than dropping the currency — an
 * amount without its unit is worse than an unstyled one.
 *
 * The whole string is LTR-isolated so its digits and the currency symbol
 * cannot be reordered against each other inside an RTL paragraph.
 */
export function formatContractAmountAr(amount: number, currency: string): string {
  const latn = { numberingSystem: 'latn', maximumFractionDigits: 2 } as const;
  try {
    return isolateLtr(
      new Intl.NumberFormat('ar-SA', { style: 'currency', currency, ...latn }).format(amount),
    );
  } catch {
    return isolateLtr(`${new Intl.NumberFormat('ar-SA', latn).format(amount)} ${currency}`);
  }
}

/** Parse an explicitly entered, non-negative contract amount without rounding it. */
export function parseContractAmountInput(raw: string): number | null {
  const value = raw.trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The download endpoint always serves verified PDF bytes. Keep that invariant
 * in the browser even when a proxy hides Content-Disposition and the caller
 * must use its local fallback name.
 */
export function contractPdfDownloadName(
  responseName: string | null | undefined,
  fallbackName: string,
): string {
  const candidate = (responseName?.trim() || fallbackName.trim() || 'contract').replace(
    /[\\/]+/g,
    '-',
  );
  const stem = candidate.replace(/\.[^.]*$/, '').trim() || 'contract';
  return `${stem}.pdf`;
}

/**
 * The effective period as one phrase.
 *
 * «حتى» ALONE IS NOT ENOUGH, and the first version of this function got it
 * backwards. An independent UX review ran the bidi algorithm over the real
 * string: the Arabic letters make rule W2 reclassify the SECOND date's
 * digits from EN to AN, its hyphens lose W4's protection, and N1 resolves
 * them as R — so `2026-09-01 حتى 2027-08-31` displays as
 * `2026-09-01 حتى 31-08-2027`. Two date formats, in one phrase, on a legal
 * surface.
 *
 * Each date is therefore wrapped in its own LTR isolate (U+2066…U+2069),
 * which is the only construct that actually fixes the internal order of a
 * Latin run regardless of what surrounds it.
 */
export function formatContractPeriodAr(from: string, to: string | null): string {
  return to ? `${isolateLtr(from)} حتى ${isolateLtr(to)}` : `${isolateLtr(from)} — مفتوح المدة`;
}

/**
 * The term, with the right Arabic plural.
 *
 * «6 شهراً» is wrong — Arabic takes the broken plural «أشهر» for 3–10 and a
 * dual/singular form below that. On a legal surface the number of months is
 * the term itself, so it is worth getting right rather than approximating
 * with the one form that suits 11+.
 */
export function formatContractTermAr(months: number | null): string {
  if (months === null) return 'غير محددة';
  if (months === 1) return 'شهر واحد';
  if (months === 2) return 'شهران';
  if (months >= 3 && months <= 10) return `${isolateLtr(String(months))} أشهر`;
  return `${isolateLtr(String(months))} شهراً`;
}

/**
 * A single date, for the release / supersession facts the client is
 * entitled to see (§3.7).
 *
 * IN THE READER'S TIMEZONE. `released_at` is a `timestamptz` set to
 * `now()`, so a release performed at 01:00 in Riyadh is stored as
 * `22:00Z` the previous day — and `toISOString()` rendered that previous
 * day to the client, on the one surface where the date has legal weight.
 * This is the same defect already fixed for the agency timeline; it simply
 * was not carried to the formatter the CLIENT reads.
 *
 * Null renders as «—» rather than as a blank cell, so a missing timestamp
 * reads as "not applicable" and not as a bug.
 *
 * IT RETURNS A BARE STRING, and the caller renders it inside {@link LtrRun}.
 * It used to return an LTR-isolated one, which a staging visual pass proved
 * insufficient: `ar-SA` embeds U+200F inside the pattern, so the reordering
 * happened INSIDE the isolate and `28/08/2026` displayed as `282026/08/`.
 * See {@link stripBidiControls}. Direction is now carried by markup, and the
 * string carries no invisible characters at all.
 */
export function formatContractDateAr(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return stripBidiControls(value);
  try {
    return stripBidiControls(
      new Intl.DateTimeFormat('ar-SA', {
        dateStyle: 'medium',
        calendar: 'gregory',
        numberingSystem: 'latn',
      }).format(parsed),
    );
  } catch {
    return parsed.toISOString().slice(0, 10);
  }
}

/** One service line, rendered as a phrase rather than a table row. */
export function formatContractServiceAr(line: ContractServiceLine): string {
  const parts: string[] = [line.name];
  if (typeof line.quantity === 'number') {
    parts.push(line.unit ? `${line.quantity} ${line.unit}` : String(line.quantity));
  } else if (line.unit) {
    parts.push(line.unit);
  }
  return parts.join(' · ');
}

/**
 * The browser-side file check. A convenience only — the server refuses
 * anything that is not a real PDF by reading its first five bytes, and
 * that check is the control (§3.3).
 */
export function validateContractFile(file: {
  readonly type: string;
  readonly size: number;
  readonly name: string;
}): string | null {
  if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
    return 'الملف يجب أن يكون بصيغة PDF.';
  }
  if (file.size <= 0) return 'الملف فارغ.';
  if (file.size > MAX_CONTRACT_BYTES) return 'حجم الملف يتجاوز 25 ميجابايت.';
  return null;
}

type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly data: ClientContractsOverviewData }
  | { readonly status: 'error'; readonly error: unknown };

/**
 * What a page action ACTUALLY achieved — Task 306A §2.6.
 *
 * `runAction` does two things in sequence: it performs the write, then it
 * reloads the contract so the screen describes the new state. Those can
 * disagree, and until this type existed every caller saw the same
 * `Promise<void>` for all three results and had to assume the good one.
 *
 * EACH VALUE SAYS EXACTLY WHAT THE HELPER PROVED, AND NOTHING MORE. Two
 * binding rounds were spent on this comment, both on the same mistake:
 * describing what the value USUALLY implies for the caller in front of it
 * rather than what it establishes for every caller. This is an exported
 * type; its comment is its contract, and it is the one artefact here that
 * no test can hold to account.
 *
 * - `not_started`    — this callback was NOT invoked, because another
 *                      action held the turnstile
 *                      (`createContractActionLock`). It says nothing about
 *                      that other action, which may have sent anything.
 * - `action_failed`  — the ACTION callback rejected, so NO completion is
 *                      established. A reload that rejects is not this: it
 *                      is `refresh_failed`, because the callback succeeded. It does NOT say the server is
 *                      untouched: an action can be several calls (the
 *                      attach is create-intent → `PUT` → complete), and a
 *                      rejection at step two or three leaves whatever step
 *                      one wrote. On the attach path that is an incomplete
 *                      `agency_client_contract_documents` row, which the
 *                      version reports as `has_pending_upload` and the
 *                      panel names on screen. Retrying is safe because the
 *                      model is append-only (303-A §2.6) and the guard
 *                      stands, not because nothing happened.
 * - `refresh_failed` — the callback RESOLVED, and no snapshot at least as
 *                      fresh as the reload that followed it reached
 *                      component state — whether that reload resolved
 *                      `false` or rejected outright. So every fact rendered on this
 *                      page may predate whatever the callback did. Whether
 *                      the server moved is the CALLBACK's business, not
 *                      this type's — `uploadContractDocument` knows it did,
 *                      because completion returned an id. This is the
 *                      dangerous value: it looks exactly like success to
 *                      anything that only checks for a thrown error.
 * - `refreshed`      — the callback resolved AND a snapshot at least as
 *                      fresh as its reload is in component state, so what
 *                      is rendered governs. "At least as fresh", not "this
 *                      read's own": a newer read may have committed first,
 *                      which `createSnapshotSequence` reports as `settled`.
 */
export type ContractActionOutcome =
  | 'not_started'
  | 'action_failed'
  | 'refresh_failed'
  | 'refreshed';

/** The page's write-then-reload helper, as passed down to every action. */
export type RunContractAction = (
  fn: () => Promise<void>,
  successMessage: string,
) => Promise<ContractActionOutcome>;

/**
 * Re-run the contract read alone, with no write. Resolves `true` only when
 * the fresh snapshot is in component state.
 */
export type RefreshContracts = () => Promise<boolean>;

const CONTRACT_REFRESH_FAILED_AR =
  'تم تنفيذ الإجراء، لكن تعذّر تحديث البيانات المعروضة. حدّث الصفحة.';
const CONTRACT_REFRESHED_AR = 'تم تحديث البيانات المعروضة.';
const CONTRACT_REFRESH_RETRY_AR = 'تعذّر تحديث البيانات المعروضة. أعد المحاولة أو حدّث الصفحة.';

/**
 * A SYNCHRONOUS single-slot claim on the page — Task 306A §2.6.
 *
 * `busy` is React state: it becomes true a render AFTER the click that set
 * it, so `disabled={busy}` cannot stop a second click dispatched in the
 * same tick, nor a handler closure retained across a re-render. Two clicks
 * on «رفع المستند والتحقق منه» therefore both passed the gate and both
 * reached `createContractUpload` — and because documents are deliberately
 * append-only (303-A §2.6), the server obligingly stored two copies of one
 * intended attach. One finishing action's `finally` also cleared the shared
 * `busy` flag while another was still in flight.
 *
 * `claim` flips a plain boolean before the first `await`, so the second
 * caller is refused in the same tick it arrived. It is not a lock in the
 * concurrency sense — JavaScript is single-threaded and there is nothing to
 * wait on — it is a turnstile: one action at a time, refused rather than
 * queued, because a queued duplicate upload is still a duplicate.
 */
export function createContractActionLock(): {
  readonly claim: () => boolean;
  readonly release: () => void;
} {
  let held = false;
  return {
    claim: () => {
      if (held) return false;
      held = true;
      return true;
    },
    release: () => {
      held = false;
    },
  };
}

/**
 * What a settled contract read may do — Task 306A §2.6.
 *
 * Three reads can be in flight at once — the mount effect, an action's
 * reload, and the explicit refresh — and they can settle in any order.
 * Without a sequence the older answer wins and the page shows a state the
 * operator has already moved past, which is the same stale snapshot §2.6
 * exists to close, one layer down.
 *
 * THREE verdicts and not two, and the third is the one a binding review
 * caught missing. A read that has been overtaken must never write; but
 * whether it may report the PAGE as up to date depends on something the
 * write permission cannot express — whether the read that overtook it
 * actually landed:
 *
 * - `commit`  — still the newest requested read. Write.
 * - `settled` — overtaken by a read that HAS committed, so a snapshot at
 *               least this fresh is already on screen. Do not write, and
 *               report the page as current, or an action that genuinely
 *               refreshed would warn that it had not — and a guard that
 *               cries wolf gets clicked through.
 * - `discard` — overtaken by a read that FAILED or has not settled. Do not
 *               write, and do not claim freshness. A two-verdict version
 *               of this collapsed `discard` into `commit`, and an older
 *               answer could then land after a newer one had already
 *               failed — the exact hazard the sequence exists to prevent.
 *
 * A counter and not an `AbortController`: the goal is to IGNORE a
 * superseded answer, not to cancel a read someone is waiting for. This is
 * also why it is not `PlatformCommercialPage.createLatestPerKey` (§1.4),
 * whose `isCurrent` answers only the write question.
 */
export type SnapshotVerdict = 'commit' | 'settled' | 'discard';

export function createSnapshotSequence(): {
  /** Take a token for a read that is about to start. */
  readonly begin: () => number;
  /** Rule on a read that has just settled successfully. */
  readonly resolve: (token: number) => SnapshotVerdict;
} {
  let requested = 0;
  let committed = 0;
  return {
    begin: () => (requested += 1),
    resolve: (token) => {
      if (token === requested) {
        committed = token;
        return 'commit';
      }
      return token <= committed ? 'settled' : 'discard';
    },
  };
}

/**
 * Everything `runContractAction` needs from the page, as data.
 *
 * The helper is extracted, and the seam is this record, because the page's
 * own `runAction` could otherwise only be PINNED as a source string: this
 * repository has no DOM test environment and no `@testing-library`, so
 * `ClientContractPage` cannot be driven. §2.5's tests drove a stand-in for
 * `runAction` whose only outcomes were *threw* and *did not throw* — and
 * §2.6 exists because the third outcome was therefore never stated. The
 * helper the tests execute is now the helper that ships.
 */
export type ContractActionRuntime = {
  readonly claim: () => boolean;
  readonly release: () => void;
  readonly isMounted: () => boolean;
  /**
   * The page's reload. Resolves `true` iff a snapshot at least this fresh
   * is in state. It MAY reject; a rejection is treated exactly like
   * `false`, because both mean nothing was committed.
   */
  readonly load: (keepOnError: boolean) => Promise<boolean>;
  readonly setBusy: (busy: boolean) => void;
  readonly setNotice: (notice: string | null) => void;
  readonly setActionError: (error: string | null) => void;
};

/**
 * Write, then reload, then say which of the four things happened.
 *
 * The order is load-bearing and it is the whole of §2.6: the reload runs
 * BEFORE the outcome is known to the caller, so no caller can retire a
 * safety guard on the strength of the write alone.
 */
export async function runContractAction(
  runtime: ContractActionRuntime,
  action: () => Promise<void>,
  successMessage: string,
): Promise<ContractActionOutcome> {
  if (!runtime.claim()) return 'not_started';
  runtime.setBusy(true);
  runtime.setActionError(null);
  runtime.setNotice(null);
  try {
    // TWO try blocks, not one, and a binding review is the reason. A single
    // `catch` around both awaits reports a REJECTED RELOAD as
    // `action_failed` — "the callback rejected", when the callback in fact
    // succeeded and it was the refresh that did not. That is the §2.6
    // defect in miniature: an outcome that under-reports how far the server
    // moved. `load` is injected, so "it never rejects" is a property of one
    // caller and not of this function.
    try {
      await action();
    } catch (err) {
      if (runtime.isMounted()) runtime.setActionError(describeContractError(err));
      return 'action_failed';
    }
    if (runtime.isMounted()) runtime.setNotice(successMessage);
    let refreshed = false;
    try {
      refreshed = await runtime.load(true);
    } catch {
      // A reload that threw committed nothing, which is exactly what a
      // reload that resolved `false` means. Same outcome, same sentence.
      refreshed = false;
    }
    if (!refreshed && runtime.isMounted()) runtime.setActionError(CONTRACT_REFRESH_FAILED_AR);
    return refreshed ? 'refreshed' : 'refresh_failed';
  } finally {
    if (runtime.isMounted()) runtime.setBusy(false);
    runtime.release();
  }
}

/**
 * The reload with no write in front of it — the escape from
 * `refresh_failed`, and the only control the panel leaves enabled while a
 * verified upload is invisible to this page (§2.6.4).
 */
export async function refreshContractData(runtime: ContractActionRuntime): Promise<boolean> {
  if (!runtime.claim()) return false;
  runtime.setBusy(true);
  runtime.setActionError(null);
  runtime.setNotice(null);
  try {
    let refreshed = false;
    try {
      refreshed = await runtime.load(true);
    } catch {
      // Never rethrown: the only caller is a click handler, and an
      // escaping rejection there is an unhandled rejection rather than a
      // message anyone reads.
      refreshed = false;
    }
    if (runtime.isMounted()) {
      if (refreshed) runtime.setNotice(CONTRACT_REFRESHED_AR);
      else runtime.setActionError(CONTRACT_REFRESH_RETRY_AR);
    }
    return refreshed;
  } finally {
    if (runtime.isMounted()) runtime.setBusy(false);
    runtime.release();
  }
}

export function ClientContractPage() {
  const { clientId } = useParams();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<ReadonlySet<string>>(() => new Set());
  // Task 303-B. Keyed by document id, like `downloading`: a single boolean
  // slot lets one finishing request re-enable another control mid-flight.
  const [acknowledging, setAcknowledging] = useState<ReadonlySet<string>>(() => new Set());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * `keepOnError` distinguishes the FIRST load from a RELOAD.
   *
   * An independent UX review found that a successful release followed by a
   * failed refresh replaced the whole page with an error state — so the
   * user was told loading failed and never learned the release worked. On
   * a reload the last good data stays on screen and the failure is
   * reported as an action-level warning instead.
   *
   * RESOLVES `true` IFF A SNAPSHOT AT LEAST THIS FRESH IS IN STATE — Task
   * 306A §2.6. That is a stronger claim than "the request succeeded", and
   * every one of §2.6's guarantees is stated in terms of it: the whole
   * point of the outcome type is that a caller may only retire a safety
   * guard when the screen has caught up.
   *
   * `createSnapshotSequence` is what makes that claim safe when three
   * reads are in flight at once and settle out of order. An overtaken read
   * never writes `state` BACKWARDS; whether it may still report the page as
   * current depends on whether the read that overtook it landed, which is
   * why the verdict has three values and not two. An aborted or unmounted
   * read reports `false`: nothing was committed and nothing is known.
   */
  const sequenceRef = useRef<ReturnType<typeof createSnapshotSequence> | null>(null);
  if (sequenceRef.current === null) sequenceRef.current = createSnapshotSequence();
  const sequence = sequenceRef.current;
  const load = useCallback(
    (signal?: AbortSignal, keepOnError = false): Promise<boolean> => {
      const token = sequence.begin();
      // Task 303-A-B — `clientId` is absent on the continuity route
      // (`/app/contracts`), and its absence IS the request: the server
      // answers for every client the caller is live-assigned to. The two
      // wrappers are separate so the wide answer can only be asked for by
      // name, never reached by dropping an argument.
      return (
        clientId ? getClientContracts({ clientId, signal }) : getMyClientContracts({ signal })
      )
        .then((data) => {
          if (signal?.aborted || !mounted.current) return false;
          const verdict = sequence.resolve(token);
          if (verdict === 'commit') setState({ status: 'ready', data });
          return verdict !== 'discard';
        })
        .catch((err: unknown) => {
          if (signal?.aborted || !mounted.current) return false;
          if (err instanceof DOMException && err.name === 'AbortError') return false;
          if (!keepOnError) setState({ status: 'error', error: err });
          return false;
        });
    },
    [clientId],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  /**
   * Task 306A §2.6 — THE OUTCOME IS RETURNED, NOT SWALLOWED, AND ONE
   * ACTION RUNS AT A TIME.
   *
   * Both helpers are module-level functions taking a runtime record,
   * because a `useCallback` body in a page with no DOM test environment
   * can only ever be PINNED as a source string — which is precisely how
   * §2.6's defect survived §2.5's "executed" tests. What ships is what the
   * tests execute; this is the wiring.
   *
   * The lock is a ref, not state: `busy` becomes true a render after the
   * click that set it, so `disabled={busy}` cannot refuse a second click
   * dispatched in the same tick.
   */
  const actionLockRef = useRef<ReturnType<typeof createContractActionLock> | null>(null);
  if (actionLockRef.current === null) actionLockRef.current = createContractActionLock();
  const actionLock = actionLockRef.current;
  const runtime = useMemo<ContractActionRuntime>(
    () => ({
      claim: actionLock.claim,
      release: actionLock.release,
      isMounted: () => mounted.current,
      load: (keepOnError: boolean) => load(undefined, keepOnError),
      setBusy,
      setNotice,
      setActionError,
    }),
    [actionLock, load],
  );

  const runAction = useCallback<RunContractAction>(
    (fn, successMessage) => runContractAction(runtime, fn, successMessage),
    [runtime],
  );

  const refreshContracts = useCallback<RefreshContracts>(
    () => refreshContractData(runtime),
    [runtime],
  );

  /**
   * The endpoint streams the PDF; this saves it.
   *
   * It deliberately does NOT open a tab. The previous version called
   * `window.open` on a signed URL after an `await` — outside the user-gesture
   * stack, so Safari blocked it routinely — and never checked the null
   * return, which made a blocked click a silent no-op. It also put the
   * storage key in the address bar (§2.10).
   *
   * WHAT THIS COSTS, STATED HONESTLY: the response is buffered end to end.
   * The Edge Function fetches the whole object, hashes it and only then
   * replies, and `kmiFetchBinary` awaits the full blob — so the browser's
   * download manager never sees progress, and for a large file on a slow
   * link the only feedback is the busy state on the button. An earlier
   * version of this comment claimed the download manager reported progress;
   * it cannot. Progressive streaming would mean serving bytes before the
   * digest check has finished, which is the one thing this endpoint exists
   * to prevent, so the buffering is deliberate and the UI compensates
   * instead.
   *
   * Task 306A §3 — THE OBJECT-URL LIFECYCLE IS NOT THIS PAGE'S BUSINESS
   * ANY MORE. It used to revoke 250 ms after the click, which cancels the
   * transfer the moment a save dialog or a slow disk outlives that timer,
   * and the failure was invisible from JavaScript. `saveBlobAsFile` owns
   * creation and revocation, revokes on `pagehide` with a 60 s ceiling, and
   * is executed by test rather than pinned as a source string.
   */
  const download = useCallback(async (documentId: string, fallbackName: string) => {
    setActionError(null);
    setNotice(null);
    setDownloading((current) => new Set(current).add(documentId));
    try {
      const file = await getContractDocumentDownload({ document_id: documentId });
      saveBlobAsFile(file.blob, contractPdfDownloadName(file.filename, fallbackName));
    } catch (err) {
      if (mounted.current) setActionError(describeContractError(err));
    } finally {
      // Keyed by document id: a single slot meant that finishing one
      // download cleared the busy state of another still in flight,
      // re-enabling a button mid-transfer.
      if (mounted.current) {
        setDownloading((current) => {
          const next = new Set(current);
          next.delete(documentId);
          return next;
        });
      }
    }
  }, []);

  /**
   * Task 303-B — record that this user reviewed the released document.
   *
   * NOT via `runAction`: that helper sets a page-wide `busy` flag the agency
   * surface uses, and the client surface must carry no agency write plumbing
   * (303-A-B §5 case 24).
   *
   * A REPLAY IS NOT AN ERROR. `replayed: true` means the server returned an
   * existing receipt and wrote nothing, so the copy must not say "recorded".
   */
  const acknowledge = useCallback(
    async (documentId: string) => {
      setActionError(null);
      setNotice(null);
      setAcknowledging((current) => new Set(current).add(documentId));
      try {
        const receipt = await acknowledgeContractVersion({
          document_id: documentId,
          // The SERVER decides which text is current; this only lets it
          // refuse a stale bundle. It is never written as evidence.
          ack_text_version: CONTRACT_ACK_TEXT_VERSION,
        });
        if (mounted.current) {
          // §2.5 — bound to ONE DOCUMENT, so «المستند», not «النسخة».
          setNotice(
            receipt.replayed
              ? 'هذا الإقرار مسجَّل لديك مسبقاً على هذا المستند.'
              : 'سُجِّل إقرارك بالاطلاع على هذا المستند.',
          );
        }
        const refreshed = await load(undefined, true);
        if (!refreshed && mounted.current) {
          // Reports only what FAILED. The success banner above already
          // says whether anything was written; on a replay this one saying
          // «سُجِّل الإقرار» would assert a write that did not happen.
          setActionError('تعذّر تحديث البيانات المعروضة. حدّث الصفحة.');
        }
      } catch (err) {
        if (mounted.current) {
          // A STALE BUNDLE IS NOT A FIELD ERROR. 22023 maps to
          // `validation_error`, whose shared copy says "check the fields" —
          // there are none here, and retrying cannot work until a reload.
          setActionError(
            isApiError(err) && err.code === 'validation_error'
              ? 'نص الإقرار المعروض لديك ليس النسخة الحالية. حدّث الصفحة ثم أعد المحاولة.'
              : describeContractError(err),
          );
        }
      } finally {
        if (mounted.current) {
          setAcknowledging((current) => {
            const next = new Set(current);
            next.delete(documentId);
            return next;
          });
        }
      }
    },
    [load],
  );

  if (state.status === 'loading') {
    return <LoadingState title="جاري تحميل العقد" />;
  }
  // A 403 and a 404 are PERMANENT here — a system_admin, an actor whose
  // assignment ended, a cross-client id, an actor without `contract.read`.
  // Telling any of them to "refresh the page" is an instruction that can
  // never work, and §6 lists permission-denied as a covered state.
  if (state.status === 'error') {
    if (isApiError(state.error) && state.error.code === 'permission_denied') {
      // 303-A-B — the default copy tells the reader to ask «مالك الوكالة»
      // for access. On the continuity route the reader is a CLIENT, who has
      // no permission to request and no relationship to the agency owner's
      // permission model.
      return clientId ? (
        <PermissionDeniedState />
      ) : (
        <PermissionDeniedState
          title="العقد غير متاح على هذا الحساب"
          description="لا يمكن عرض عقد الخدمات على هذا الحساب. تواصل مع الوكالة التي تدير حسابك."
        />
      );
    }
    if (isApiError(state.error) && state.error.code === 'not_found') {
      return (
        <NotFoundState
          title="العقد غير متاح"
          description={
            clientId
              ? 'لا يوجد عقد متاح لك على هذا العميل، أو أن نطاقك لا يشمله.'
              : 'لا يوجد عقد متاح لهذا الحساب.'
          }
          // 303-A-B — on the continuity route `/app/clients` is EMPTY for
          // this actor (the clients read is organization-gated too), so
          // sending them there would say "your agency has no clients" to a
          // client of an agency that has plenty.
          homePath={clientId ? '/app/clients' : '/app'}
        />
      );
    }
    return (
      <ErrorState
        title="تعذر تحميل العقد"
        description={
          clientId
            ? 'حدث خطأ أثناء تحميل عقد هذا العميل. حاول مرة أخرى.'
            : 'حدث خطأ أثناء تحميل العقد. حاول مرة أخرى.'
        }
        error={state.error}
        onRetry={() => {
          setState({ status: 'loading' });
          void load();
        }}
      />
    );
  }

  const { data } = state;

  return (
    <div className="stack-md">
      <ContractIntro viewer={data.viewer} />

      {/* Both regions are mounted UNCONDITIONALLY and only their text
          changes. A live region inserted at the same moment as its content
          is frequently not announced at all by NVDA, JAWS or VoiceOver —
          the node has to be in the accessibility tree before the change it
          is meant to report. */}
      <div
        className={notice ? 'banner banner-success' : undefined}
        role="status"
        aria-live="polite"
        hidden={!notice}
      >
        {notice}
      </div>
      <div
        className={actionError ? 'banner banner-error' : undefined}
        role="alert"
        aria-live="assertive"
        hidden={!actionError}
      >
        {actionError}
      </div>

      {data.viewer === 'agency' && clientId ? (
        <AgencySurface
          data={data}
          clientId={clientId}
          busy={busy}
          onDownload={download}
          downloadingIds={downloading}
          runAction={runAction}
          onRefresh={refreshContracts}
        />
      ) : data.viewer === 'agency' ? (
        // 303-A-B — the agency projection is only ever served on the
        // client-scoped route. The continuity route is client-only and the
        // server refuses agency actors there, so this branch is
        // unreachable in practice; it exists so the surface cannot render
        // without the client id its write actions require.
        <PermissionDeniedState />
      ) : (
        <ClientSurface
          data={data}
          onDownload={download}
          downloadingIds={downloading}
          onAcknowledge={acknowledge}
          acknowledgingIds={acknowledging}
        />
      )}
    </div>
  );
}

/**
 * The disambiguation paragraph. It is not decoration: 295A §11.B1 records
 * that "contract", "package" and "subscription" are routinely confused in
 * this product, and this is the one surface where getting it wrong has
 * legal weight.
 */
function ContractIntro({ viewer }: { readonly viewer: 'agency' | 'client' }) {
  return (
    <section className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>عقد الخدمات</h2>
      </div>
      <p className="body" style={{ margin: 0 }}>
        {viewer === 'agency'
          ? 'هذه صفحة العقد المبرم بين الوكالة وهذا العميل. يُرفع هنا مستند العقد الذي وُثّق خارج المنصة، مع لقطة ثابتة للباقة والخدمات والمبلغ والمدة كما اتُّفق عليها.'
          : 'هذه صفحة عقد الخدمات المبرم بينك وبين الوكالة. المستند المعروض هنا وُثّق خارج المنصة، ويظهر معه ما اتُّفق عليه من خدمات ومبلغ ومدة.'}
      </p>
      {/* Two words changed after an independent UX review, both in the one
          sentence that carries legal weight. «من هذه الصفحة» invited "then
          from where?" when §2.9 is absolute, and «خارجها» attached its
          pronoun to «هذه الصفحة» rather than to «المنصة» — reading as
          "executed outside this page", a far weaker claim than the one §1
          requires. Both nouns are now spelled out. */}
      <p className="tiny" style={{ marginTop: 10, marginBottom: 0 }}>
        لا يتم التوقيع داخل المنصة، ولا تُجرى أي عملية دفع داخل المنصة. الملف المرفوع هو إثبات لعقد
        وُثّق خارج المنصة.
      </p>
    </section>
  );
}

// =====================================================================
// Agency surface
// =====================================================================

function AgencySurface({
  data,
  clientId,
  busy,
  onDownload,
  downloadingIds,
  runAction,
  onRefresh,
}: {
  readonly data: AgencyContractOverviewData;
  readonly clientId: string;
  readonly busy: boolean;
  readonly onDownload: (documentId: string, fallbackName: string) => void;
  readonly downloadingIds?: ReadonlySet<string>;
  readonly runAction: RunContractAction;
  readonly onRefresh: RefreshContracts;
}) {
  const [creating, setCreating] = useState(false);

  return (
    <>
      {data.client_archived ? (
        <div className="banner banner-info" role="status">
          هذا العميل مؤرشف. العقود تُقرأ وتُنزّل، ولا تقبل أي تعديل أو إضافة.
        </div>
      ) : null}

      {data.can_manage ? (
        <section className="card" style={{ padding: 22 }}>
          <div className="sec-title">
            <h2>تسجيل عقد جديد</h2>
            <button type="button" className="btn btn-text" onClick={() => setCreating((v) => !v)}>
              {creating ? 'إخفاء' : 'إضافة عقد'}
            </button>
          </div>
          {creating ? (
            <ContractSnapshotForm
              busy={busy}
              submitLabel="حفظ العقد كمسودة"
              withTitle
              onSubmit={async (payload) => {
                // The outcome is deliberately discarded here: this form's
                // behaviour is unchanged by §2.6, and awaiting it keeps the
                // submit button's busy state exactly as it was.
                await runAction(async () => {
                  await createAgencyClientContract({ client_id: clientId, ...payload });
                  setCreating(false);
                }, 'تم إنشاء العقد كمسودة. ارفع المستند الموثّق ثم اعتمده.');
              }}
            />
          ) : (
            <p className="tiny" style={{ margin: 0 }}>
              يُسجَّل العقد كمسودة أولاً، ثم يُرفع مستند PDF الموثّق خارج المنصة، ثم يُعتمد ليصبح
              العقد الساري الظاهر للعميل.
            </p>
          )}
        </section>
      ) : null}

      {data.contracts.length === 0 ? (
        <EmptyState
          title="لا يوجد عقد مسجّل لهذا العميل"
          description={
            data.can_manage
              ? 'ابدأ بتسجيل عقد جديد، ثم ارفع نسخة PDF من العقد الموثّق خارج المنصة.'
              : 'لا يوجد عقد مسجّل لهذا العميل. التسجيل متاح لمالك الوكالة ومدير التسويق، وعلى عميل غير مؤرشف.'
          }
        />
      ) : (
        data.contracts.map((contract) => (
          <AgencyContractCard
            key={contract.id}
            contract={contract}
            canManage={data.can_manage}
            canDownload={data.can_download}
            busy={busy}
            onDownload={onDownload}
            downloadingIds={downloadingIds}
            runAction={runAction}
            onRefresh={onRefresh}
          />
        ))
      )}
    </>
  );
}

function AgencyContractCard({
  contract,
  canManage,
  canDownload,
  busy,
  onDownload,
  downloadingIds,
  runAction,
  onRefresh,
}: {
  readonly contract: AgencyContract;
  readonly canManage: boolean;
  readonly canDownload: boolean;
  readonly busy: boolean;
  readonly onDownload: (documentId: string, fallbackName: string) => void;
  readonly downloadingIds?: ReadonlySet<string>;
  readonly runAction: RunContractAction;
  readonly onRefresh: RefreshContracts;
}) {
  const [replacing, setReplacing] = useState(false);
  const current = contract.versions.find((v) => v.id === contract.current_version_id) ?? null;
  const draft = contract.versions.find((v) => v.status === 'draft') ?? null;
  const history = contract.versions.filter((v) => v.status === 'superseded');
  // An archived contract accepts no write; the server refuses it too.
  const manageable = canManage && !contract.archived;

  return (
    <section className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>{contract.title}</h2>
        <span className={`pill ${contract.archived ? 'pill-archived' : 'pill-active'}`}>
          {contract.archived ? 'مؤرشف' : 'قائم'}
        </span>
      </div>

      {contract.archived ? (
        <p className="tiny" style={{ marginTop: 0 }}>
          العقد مؤرشف: يبقى قابلاً للقراءة والتنزيل، ولا يقبل نسخة جديدة أو اعتماداً.
        </p>
      ) : null}

      {current ? (
        <>
          <VersionBlock
            version={current}
            heading={CONTRACT_RELEASED_NOUN_AR}
            canDownload={canDownload}
            onDownload={onDownload}
            downloadingIds={downloadingIds}
          />
          <AgencyAcknowledgementList acknowledgements={current.acknowledgements} />
        </>
      ) : (
        <p className="body">لا توجد {CONTRACT_RELEASED_NOUN_INDEF_AR} بعد.</p>
      )}

      {draft ? (
        <DraftVersionBlock
          version={draft}
          manageable={manageable}
          busy={busy}
          canDownload={canDownload}
          onDownload={onDownload}
          downloadingIds={downloadingIds}
          runAction={runAction}
          onRefresh={onRefresh}
        />
      ) : null}

      {history.length > 0 ? (
        <div style={{ marginTop: 18 }}>
          <div className="lbl" style={{ marginBottom: 8 }}>
            النسخ السابقة
          </div>
          {history.map((version) => (
            <div key={version.id}>
              <VersionBlock
                version={version}
                heading={`النسخة ${version.version_number}`}
                canDownload={canDownload}
                onDownload={onDownload}
                downloadingIds={downloadingIds}
                compact
              />
              <AgencyAcknowledgementList acknowledgements={version.acknowledgements} />
            </div>
          ))}
        </div>
      ) : null}

      {manageable && !draft ? (
        <div style={{ marginTop: 18 }}>
          <button type="button" className="btn btn-text" onClick={() => setReplacing((v) => !v)}>
            {replacing ? 'إلغاء' : 'تسجيل نسخة بديلة'}
          </button>
          {replacing ? (
            <ContractSnapshotForm
              busy={busy}
              submitLabel="حفظ النسخة الجديدة كمسودة"
              onSubmit={async (payload) => {
                await runAction(async () => {
                  await createAgencyClientContract({ contract_id: contract.id, ...payload });
                  setReplacing(false);
                }, 'تم إنشاء نسخة جديدة كمسودة. النسخة السابقة تبقى محفوظة.');
              }}
            />
          ) : null}
        </div>
      ) : null}

      {canManage ? (
        <div style={{ marginTop: 18, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={busy}
            onClick={() =>
              void runAction(
                async () => {
                  await setContractArchiveState({
                    contract_id: contract.id,
                    archived: !contract.archived,
                  });
                },
                contract.archived ? 'تمت إعادة العقد إلى القائمة.' : 'تمت أرشفة العقد.',
              )
            }
          >
            {contract.archived ? 'إلغاء الأرشفة' : 'أرشفة العقد'}
          </button>
          <span className="tiny">لا يوجد حذف: الأرشفة والاستبدال فقط.</span>
        </div>
      ) : null}

      {contract.events.length > 0 ? <ContractTimeline contract={contract} /> : null}
    </section>
  );
}

/**
 * The draft version, and the step that makes it real.
 *
 * Task 306A §2 — WHY THIS WAS REBUILT. The three upload boundaries were
 * already correct and already wired; what was missing was the STEP. The
 * control was a bare `<input type="file">` below the draft's fact grid,
 * inside a block that renders identically for a released version, on a
 * card whose visual weight sits entirely on the released version above it.
 * It also uploaded on `change`, so choosing a file WAS the commit: there
 * was no moment where the user could see which file they were about to
 * attach, and the only sentence naming the next step was a success banner
 * several cards up the page that disappears on the next action.
 *
 * So: an explicitly labelled step panel, at the TOP of the draft block,
 * and selection and upload are two separate actions.
 *
 * RELEASE STAYS A SEPARATE, EXPLICIT ACTION. Attaching a document does not
 * release it, and nothing here auto-releases anything. The release button
 * keeps its own gate — the latest upload must be complete and verified,
 * AND nothing may be sitting chosen-but-unsent, because releasing then
 * releases the document the operator is visibly in the middle of replacing
 * (§2.5) — and 303-A §2.4 still forbids any word that would make either
 * action read as a signature.
 *
 * IMMUTABILITY IS UNCHANGED. Re-uploading creates a NEW document row; it
 * never mutates the completed one, and `20260915120000` freezes
 * `current_document_id` on that path. Replacement still appends a version.
 * There is still no delete control (303-A §2.6).
 */
function DraftVersionBlock({
  version,
  manageable,
  busy,
  canDownload,
  onDownload,
  downloadingIds,
  runAction,
  onRefresh,
}: {
  readonly version: AgencyContractVersion;
  readonly manageable: boolean;
  readonly busy: boolean;
  readonly canDownload: boolean;
  readonly onDownload: (documentId: string, fallbackName: string) => void;
  readonly downloadingIds?: ReadonlySet<string>;
  readonly runAction: RunContractAction;
  readonly onRefresh: RefreshContracts;
}) {
  const ready = version.document?.completed_at != null;
  const uploadNeedsRetry = version.has_pending_upload || version.has_expired_upload;

  return (
    <div
      style={{
        marginTop: 18,
        paddingTop: 14,
        borderTop: '1px dashed var(--line-2)',
      }}
    >
      {manageable ? (
        <ManagedContractDocumentStep
          // The chosen file belongs to THIS draft. Without the key, a
          // refresh that replaces one draft version with another — another
          // operator released this one and started the next — would carry a
          // File chosen for the old version onto the new `version_id`,
          // which is the same wrong-document mismatch §2.5 exists to
          // prevent, one level up.
          key={version.id}
          version={version}
          busy={busy}
          ready={ready}
          uploadNeedsRetry={uploadNeedsRetry}
          runAction={runAction}
          onRefresh={onRefresh}
        />
      ) : null}

      <VersionBlock
        version={version}
        heading="مسودة قيد التجهيز"
        canDownload={canDownload}
        onDownload={onDownload}
        downloadingIds={downloadingIds}
        compact
      />
    </div>
  );
}

/**
 * Owns the chosen-but-unsent file, and owns it at exactly the scope where
 * that file means anything: inside the `manageable` branch, keyed by the
 * draft version.
 *
 * WHY THIS WRAPPER EXISTS RATHER THAN A `useState` IN EITHER NEIGHBOUR.
 * `ContractDocumentStep` must take the selection as a PROP, because the
 * release gate reads it and a gate whose input is private state can only
 * be asserted by grepping the source (§2.5). But holding it in
 * `DraftVersionBlock` — which renders for a non-manager and for an
 * archived contract too — outlives the panel: archive the contract with a
 * file chosen, unarchive it, and the old filename returns beside a file
 * input the browser has just re-created empty. Before the selection became
 * a prop, hiding the panel unmounted it and that could not happen.
 *
 * So the state sits in the smallest component whose lifetime is exactly
 * the selection's: mounted only while the contract is manageable, remounted
 * when the draft identity changes. The panel stays prop-driven and
 * renderable; the lifetime is restored to what it was.
 */
function ManagedContractDocumentStep({
  version,
  busy,
  ready,
  uploadNeedsRetry,
  runAction,
  onRefresh,
}: {
  readonly version: AgencyContractVersion;
  readonly busy: boolean;
  readonly ready: boolean;
  readonly uploadNeedsRetry: boolean;
  readonly runAction: RunContractAction;
  readonly onRefresh: RefreshContracts;
}) {
  const [selection, setSelection] = useState<ContractFileSelection>(NO_CONTRACT_FILE_SELECTED);
  return (
    <ContractDocumentStep
      version={version}
      busy={busy}
      ready={ready}
      uploadNeedsRetry={uploadNeedsRetry}
      selection={selection}
      onSelectionChange={setSelection}
      runAction={runAction}
      onRefresh={onRefresh}
    />
  );
}

/**
 * Where a chosen file stands with respect to the SERVER — Task 306A §2.6.
 *
 * A union rather than two independent fields, because the states are
 * mutually exclusive and the pair `{file, error}` alone could express
 * combinations that mean nothing («a file AND a refusal»). The two data
 * fields survive on every member so `selection.file` / `selection.error`
 * stay readable without narrowing.
 *
 * - `none`        — nothing chosen.
 * - `refused`     — the client-side validator rejected the choice. There is
 *                   no unsent file, so this blocks upload and NOT release.
 * - `unsent`      — chosen in this browser; the server has never seen it.
 * - `unconfirmed` — the server CREATED, received and verified it, and the
 *                   reload that would have put it on screen failed. Every
 *                   fact this panel renders — including `ready` — still
 *                   describes the document from BEFORE the upload.
 *                   `documentId` is what the server verified, so the guard
 *                   can retire itself the moment the page can see it.
 */
export type ContractFileSelection =
  | { readonly status: 'none'; readonly file: null; readonly error: null }
  | { readonly status: 'refused'; readonly file: null; readonly error: string }
  | { readonly status: 'unsent'; readonly file: File; readonly error: null }
  | {
      readonly status: 'unconfirmed';
      readonly file: File;
      readonly error: null;
      readonly documentId: string;
    };

export const NO_CONTRACT_FILE_SELECTED: ContractFileSelection = {
  status: 'none',
  file: null,
  error: null,
};

/** What the panel may show and may do, derived from one place. */
export type ContractStepGate = {
  /** The file to DISPLAY as chosen. Null once a verified upload is visible. */
  readonly file: File | null;
  /** Chosen here, never sent. */
  readonly pendingSelection: boolean;
  /** Verified by the server, invisible to this page's data. */
  readonly unconfirmedUpload: boolean;
  /** May «رفع المستند والتحقق منه» send? */
  readonly canSend: boolean;
  /** May «اعتماد النسخة» be clicked? */
  readonly canRelease: boolean;
};

/**
 * The one decision, as a pure function — Task 306A §2.5, §2.6.
 *
 * Exported and executed rather than only rendered, because a `disabled`
 * attribute is a hint and not a guard: the handler behind it is still
 * reachable from a click that races the re-render. `upload` calls this too,
 * so the refusal is enforced where it matters and asserted where it can be
 * enumerated.
 *
 * `unconfirmedUpload` compares the verified document id against the one the
 * page currently HAS. That makes the guard self-retiring: any refresh that
 * brings the verified document into view — this panel's own button, or any
 * other action on the page — ends it, and a refresh that brings something
 * ELSE (another operator uploaded meanwhile) does not, which is the
 * conservative answer and the right one.
 */
export function contractStepGate({
  ready,
  uploadNeedsRetry,
  selection,
  currentDocumentId,
}: {
  readonly ready: boolean;
  readonly uploadNeedsRetry: boolean;
  readonly selection: ContractFileSelection;
  readonly currentDocumentId: string | null;
}): ContractStepGate {
  const pendingSelection = selection.status === 'unsent';
  // BOTH facts, not just the id. `ready` and `currentDocumentId` arrive
  // here as INDEPENDENT inputs, so this function can be called — and, being
  // exported, will be — with a matching id and an incomplete document. On
  // the id alone that state retires the guard, hides the file, refuses the
  // send AND removes the refresh control, while `ready === false` keeps
  // release refused: an operator with no control left but a browser reload.
  // The server writes `current_document_id` and `completed_at` in one
  // transaction (`20260911120000:1552-1560`), so that snapshot should not
  // reach a browser; a guard that costs one conjunct is not worth making
  // conditional on that holding.
  const unconfirmedUpload =
    selection.status === 'unconfirmed' && (selection.documentId !== currentDocumentId || !ready);
  // A settled `unconfirmed` selection is history: the file it names is the
  // document now on screen, verified, so the panel must stop offering to
  // send it — sending it again would append a second copy of the same PDF.
  const settled = selection.status === 'unconfirmed' && !unconfirmedUpload;
  const file = settled ? null : selection.file;
  return {
    file,
    pendingSelection,
    unconfirmedUpload,
    canSend: file !== null && !unconfirmedUpload,
    canRelease: ready && !uploadNeedsRetry && !pendingSelection && !unconfirmedUpload,
  };
}

/**
 * The attach sequence: create intent → signed `PUT` → server-side verify.
 *
 * Extracted from the component so that "a completed upload — and ONLY a
 * completed upload — clears the pending selection" is EXECUTED by test
 * rather than pinned as a source string. The three boundaries and their
 * order are unchanged (303-A §3.3): nothing the browser claims about the
 * file is taken as evidence; the server fetches the object, checks the
 * `%PDF-` magic bytes and computes the digest itself.
 *
 * NEITHER CALLBACK RUNS INSIDE THE `runAction` CALLBACK — Task 306A §2.6,
 * and an earlier version of this function got that wrong.
 *
 * It used to call `onUploaded()` on the line after `completeContractUpload`
 * resolved, i.e. still inside the action, BEFORE `runAction` reloads the
 * contract. `runAction` does not rethrow, so a reload that failed after a
 * successful completion looked identical to a clean success from in here:
 * the chosen-file guard was already retired, the props still described the
 * PREVIOUS document, `busy` went back to false, and «اعتماد النسخة»
 * re-enabled itself from a snapshot that predated the upload. One click
 * then released the newly current server document while the page still
 * named the old file — the §2.5 mismatch again, reached down a different
 * path.
 *
 * So the completion result is carried OUT of the action and dispatched on
 * the outcome `runAction` reports:
 *
 * - nothing was sent (`not_started`), or completion threw, or the `PUT`
 *   did → neither callback; the selection stands, release stays blocked,
 *   and retrying is a fresh attempt. `completedDocumentId` is the
 *   discriminator, not the outcome: only a completion that RETURNED an id
 *   dispatches anything.
 * - completion succeeded, refresh failed → `onUnconfirmed`, with the id of
 *   the document the server verified. Here — unlike the generic outcome —
 *   the server demonstrably HAS moved, because completion answered with
 *   that id. The guard is NOT retired; it changes meaning, from "unsent" to
 *   "sent, and this page cannot see it".
 * - completion succeeded and a snapshot at least as fresh as its reload is
 *   in state → `onUploaded`, and only then.
 */
export async function uploadContractDocument({
  versionId,
  file,
  runAction,
  onUploaded,
  onUnconfirmed,
}: {
  readonly versionId: string;
  readonly file: File;
  readonly runAction: RunContractAction;
  readonly onUploaded: () => void;
  readonly onUnconfirmed: (completion: { readonly documentId: string }) => void;
}): Promise<ContractActionOutcome> {
  let completedDocumentId: string | null = null;
  const outcome = await runAction(async () => {
    const ticket = await createContractUpload({
      version_id: versionId,
      filename: file.name,
      byte_size: file.size,
      content_type: 'application/pdf',
    });
    const put = await fetch(ticket.upload_url, {
      method: 'PUT',
      headers: { ...ticket.required_headers },
      body: file,
    });
    if (!put.ok) {
      throw new Error('upload_failed');
    }
    // The server now fetches the object itself, checks the %PDF- magic
    // bytes and computes the digest. Nothing the browser claimed about
    // the file is taken as evidence.
    const completed = await completeContractUpload({ document_id: ticket.document_id });
    completedDocumentId = completed.document_id;
  }, 'تم رفع المستند والتحقق منه على الخادم.');

  // `action_failed` cannot have set this, and a completion that resolved
  // without a document id is not evidence of anything — both fall through
  // to "no callback", which is the safe direction: the guard stands.
  if (completedDocumentId === null) return outcome;
  if (outcome === 'refreshed') onUploaded();
  else onUnconfirmed({ documentId: completedDocumentId });
  return outcome;
}

/**
 * Release, refused unless the gate allows it — Task 306A §2.6.
 *
 * `disabled={… || !canRelease}` is a HINT. The handler behind it survives
 * the re-render that would remove it, so a click dispatched in the same
 * tick as the state change that closed the gate still reaches this code —
 * which is the §2.5 wrong-document release, arrived at by a route the
 * attribute cannot cover. The gate is therefore consulted here too, and
 * because this is a function rather than a closure inside the panel, that
 * refusal is executed by test rather than read out of the source.
 *
 * It performs no upload, touches no selection and creates nothing: release
 * is and stays a separate, explicit action (303-A §2.4).
 */
export async function releaseContractDocument({
  versionId,
  gate,
  runAction,
}: {
  readonly versionId: string;
  readonly gate: ContractStepGate;
  readonly runAction: RunContractAction;
}): Promise<ContractActionOutcome> {
  if (!gate.canRelease) return 'not_started';
  return runAction(async () => {
    await releaseContractVersion({ version_id: versionId });
  }, 'تم اعتماد النسخة. أصبحت هي العقد الساري الظاهر للعميل.');
}

/**
 * The attach-then-release step, as one region.
 *
 * "What is blocking release" used to be spread across three places — a
 * warning paragraph, a disabled button, and a hint beside it. It is one
 * region now, in the order the work happens: choose, upload, release.
 */
export function ContractDocumentStep({
  version,
  busy,
  ready,
  uploadNeedsRetry,
  selection,
  onSelectionChange,
  runAction,
  onRefresh,
}: {
  readonly version: AgencyContractVersion;
  readonly busy: boolean;
  readonly ready: boolean;
  readonly uploadNeedsRetry: boolean;
  readonly selection: ContractFileSelection;
  readonly onSelectionChange: (next: ContractFileSelection) => void;
  readonly runAction: RunContractAction;
  readonly onRefresh: RefreshContracts;
}) {
  const [uploading, setUploading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const refreshRef = useRef<HTMLButtonElement | null>(null);
  const inputId = `contract-file-${version.id}`;
  const helpId = `contract-file-help-${version.id}`;

  /**
   * A CHOSEN-BUT-UNSENT FILE BLOCKS RELEASE (independent review, P2).
   *
   * `ready` describes the document the SERVER has verified and stored; the
   * selection describes a file that exists only in this browser. On a
   * draft that already carries a verified document, picking a replacement
   * and not uploading it left «اعتماد النسخة» enabled, so one click
   * released the OLD document while the NEW filename was on screen beside
   * it. The two things the operator can see disagreed, and the control
   * followed the one they had just replaced.
   *
   * The gate is not "has the replacement finished", because that state is
   * unknowable here — it is "is there anything chosen that this page's data
   * does not already account for". Uploading AND refreshing clears it;
   * «إزالة الاختيار» clears it; a failed upload does not, and — §2.6 — a
   * SUCCEEDED upload whose refresh failed does not either, because then
   * every fact rendered here, `ready` included, predates the upload.
   *
   * This does NOT merge the two actions: upload and release remain
   * separate, explicit, separately-labelled buttons, and nothing on the
   * upload path releases anything (303-A §2.4).
   */
  const gate = contractStepGate({
    ready,
    uploadNeedsRetry,
    selection,
    currentDocumentId: version.document?.id ?? null,
  });
  const { pendingSelection, unconfirmedUpload, canRelease } = gate;
  const selected = gate.file;
  const fileError = selection.error;
  // Nothing but «تحديث البيانات» may act while the verified upload is
  // invisible to this page: picking a new file would silently discard the
  // warning, sending again would append a second copy of the same PDF, and
  // clearing the selection would re-open release against the stale `ready`.
  const frozen = busy || uploading || refreshing || unconfirmedUpload;

  /**
   * Selection VALIDATES but does not send. `validateContractFile` is a
   * convenience — the server reads the first five bytes and recomputes the
   * digest, and that is the control (303-A §3.3) — but running it here
   * means a non-PDF or an oversize file is refused before a 25 MiB PUT,
   * not after one.
   */
  const select = (file: File | null) => {
    // §2.6 — a new choice would replace the unconfirmed-upload guard with
    // an ordinary pending selection and lose the warning. The picker is
    // disabled in that state; this is the same refusal in the handler.
    if (unconfirmedUpload) return;
    if (!file) {
      onSelectionChange(NO_CONTRACT_FILE_SELECTED);
      return;
    }
    const problem = validateContractFile(file);
    onSelectionChange(
      problem
        ? { status: 'refused', file: null, error: problem }
        : { status: 'unsent', file, error: null },
    );
  };

  const clearSelection = () => {
    onSelectionChange(NO_CONTRACT_FILE_SELECTED);
    if (inputRef.current) inputRef.current.value = '';
  };

  /** «إزالة الاختيار» — refused while the guard is live, for the same reason. */
  const clearChoice = () => {
    if (unconfirmedUpload) return;
    clearSelection();
  };

  const upload = async () => {
    const file = gate.file;
    // The gate, not the `disabled` attribute, is the guard: a disabled
    // button is a hint, and its handler is still reachable from a click
    // that races the re-render.
    if (!file || !gate.canSend) return;
    // Re-validated at the moment of sending, not only at selection: the
    // File handle can outlive the picker, and a check that only ran on a
    // path the caller controls is not a check.
    const problem = validateContractFile(file);
    if (problem) {
      onSelectionChange({ status: 'refused', file: null, error: problem });
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    onSelectionChange({ status: 'unsent', file, error: null });
    setUploading(true);
    try {
      await uploadContractDocument({
        versionId: version.id,
        file,
        runAction,
        // Only a verified upload WHOSE REFRESH LANDED retires the
        // selection — and retiring it is what re-opens release, now for
        // the document the server has just confirmed rather than the one
        // it replaced.
        onUploaded: clearSelection,
        // Verified, but this page still shows the previous document. The
        // guard is not retired; it is re-labelled, and the panel switches
        // to offering the refresh that is the only safe way forward.
        onUnconfirmed: ({ documentId }) =>
          onSelectionChange({ status: 'unconfirmed', file, error: null, documentId }),
      });
    } finally {
      setUploading(false);
    }
  };

  /**
   * §2.6 — the recovery, and it is a READ.
   *
   * Re-sending the file is the one thing that must not happen here: the
   * server already holds it, verified, and a second attach appends a second
   * document row (303-A §2.6 — nothing is overwritten, nothing is deleted).
   * So this only re-asks for the contract. The selection is cleared on
   * success as tidying, not as the guard — `contractStepGate` retires the
   * guard on the document id, so a refresh that landed ends it either way.
   */
  const refreshNow = async () => {
    setRefreshing(true);
    try {
      if (await onRefresh()) clearSelection();
    } finally {
      setRefreshing(false);
    }
  };

  /** Release, through the shared refusal. See `releaseContractDocument`. */
  const release = () => releaseContractDocument({ versionId: version.id, gate, runAction });

  /**
   * THE UPLOAD BUTTON GOES DISABLED UNDER THE OPERATOR'S OWN CURSOR — Task
   * 306A §2.6, an independent-frame review finding.
   *
   * Entering the unconfirmed state disables «رفع المستند والتحقق منه», which
   * is the control the operator was standing on: they just pressed it. A
   * browser drops focus to `document.body` when the focused element becomes
   * disabled, and the only control that can END this state — «تحديث
   * البيانات» — sits EARLIER in the DOM, so tabbing forward from nowhere
   * walks away from the one thing that helps. A keyboard user would be left
   * in a state whose whole design is "one way out", with the way out behind
   * them.
   *
   * Focus is moved only when it is safe to move: the panel entered the
   * state, and focus is either lost (`body`) or still inside this panel. An
   * operator who has deliberately tabbed elsewhere is not yanked back.
   *
   * NOT EXECUTED, AND SAID SO: this repository has no DOM test environment,
   * so the tests render to static markup and effects never run. §2.6.7
   * counts this among the source-pinned properties.
   */
  useEffect(() => {
    if (!unconfirmedUpload) return;
    const active = typeof document === 'undefined' ? null : document.activeElement;
    const inside = active !== null && sectionRef.current?.contains(active) === true;
    if (active !== null && active !== document.body && !inside) return;
    refreshRef.current?.focus();
  }, [unconfirmedUpload]);

  return (
    <section
      ref={sectionRef}
      className="card"
      style={{ padding: 16, marginBottom: 14, background: 'var(--cream)' }}
      aria-label="خطوات إتمام المسودة"
      data-testid={`contract-document-step-${version.id}`}
    >
      <div className="lbl" style={{ marginBottom: 6 }}>
        {/* The chosen file wins the heading. Saying «بقي الاعتماد» while an
            unsent file is named on screen is the same mismatch the release
            gate now refuses, one line higher up. §2.6 puts the unconfirmed
            upload FIRST: it is the only state in which what is rendered
            below this line is known to be out of date. */}
        {unconfirmedUpload
          ? 'تم رفع المستند والتحقق منه · لم تُحدَّث البيانات المعروضة'
          : pendingSelection
            ? 'ملف مُختار لم يُرفع بعد · ارفعه قبل الاعتماد'
            : ready && !uploadNeedsRetry
              ? 'المستند مرفوع ومُتحقَّق منه · بقي الاعتماد'
              : 'الخطوة التالية · إرفاق مستند العقد'}
      </div>
      <p className="tiny" style={{ marginTop: 0, marginBottom: 12 }}>
        المسودة تحفظ ما اتُّفق عليه فقط. أرفق ملف PDF للعقد الموثّق خارج المنصة، ثم اعتمد النسخة
        لتصبح هي العقد الساري الظاهر للعميل.
      </p>

      {/* §2.6 — the upload landed and the page did not. Said first, said
          plainly, and said with the one control that can end it. This is
          NOT «أعد الرفع»: the file is on the server and verified, and a
          second attach would append a second copy of it. */}
      {unconfirmedUpload ? (
        <div
          className="banner banner-warn"
          role="alert"
          style={{ marginBottom: 12 }}
          data-testid={`contract-upload-unconfirmed-${version.id}`}
        >
          <p className="tiny" style={{ marginTop: 0, marginBottom: 8 }}>
            رُفع الملف وتحقّق منه الخادم، ولم تعكس البيانات المعروضة في هذه الصفحة ذلك بعد. ما تراه
            أدناه يصف الحالة قبل الرفع، فلا تعتمد النسخة قبل التحديث. لا تُعِد الرفع — الملف محفوظ،
            وإعادة رفعه تُنشئ نسخة ثانية منه.
          </p>
          <button
            ref={refreshRef}
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy || uploading || refreshing}
            aria-busy={refreshing}
            data-testid={`contract-refresh-${version.id}`}
            onClick={() => void refreshNow()}
          >
            تحديث البيانات
          </button>
        </div>
      ) : null}

      {/* A version whose PUT landed but whose completion call did not leaves
          `document: null` with `has_pending_upload` true. Without this line
          the draft rendered «لم يُرفق مستند بعد» — flatly untrue — and the
          operator had no way to know a retry is a replay rather than a first
          attempt. §10 files the orphan sweep; this is the row it hides. */}
      {/* Fires on `has_pending_upload` ALONE. Gating it on
          `document == null` suppressed it in the worse case: after a
          REPLACEMENT upload whose PUT landed but whose completion failed,
          the version still points at the previous completed file, so the
          operator saw the old filename, no warning, and an enabled
          «اعتماد النسخة» — and would release the file they had just tried to
          replace. The two cases need different sentences, not one gate. */}
      {version.has_expired_upload ? (
        <p className="tiny" style={{ marginTop: 0, marginBottom: 12, color: 'var(--amber-ink)' }}>
          انتهت صلاحية أحدث محاولة رفع قبل اكتمال التحقق. أعد رفع ملف PDF قبل اعتماد النسخة.
        </p>
      ) : version.has_pending_upload ? (
        <p className="tiny" style={{ marginTop: 0, marginBottom: 12, color: 'var(--amber-ink)' }}>
          {version.document == null
            ? 'هناك رفع سابق لم يكتمل التحقق منه. أعد رفع الملف — لن يُحذف شيء، ستُنشأ نسخة جديدة منه.'
            : 'هناك رفع أحدث لم يكتمل التحقق منه. المستند المعروض أدناه هو الملف السابق — أعد الرفع قبل الاعتماد.'}
        </p>
      ) : null}

      <div className="stack-sm">
        <label className="lbl" htmlFor={inputId}>
          {ready ? 'استبدال المستند المرفوع' : 'رفع مستند العقد (PDF)'}
        </label>
        {/* The native control is the accessible one and stays the control:
            it keeps the label, the id, the described-by and the keyboard
            behaviour. Only its presentation is replaced, and the button
            below merely forwards a click to it. */}
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept="application/pdf"
          disabled={frozen}
          aria-invalid={fileError !== null}
          aria-describedby={helpId}
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
          onChange={(e) => select(e.target.files?.[0] ?? null)}
        />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={frozen}
            data-testid={`contract-file-pick-${version.id}`}
            onClick={() => inputRef.current?.click()}
          >
            اختيار ملف PDF
          </button>
          {selected ? (
            <>
              <bdi className="tiny" data-testid={`contract-file-selected-${version.id}`}>
                {selected.name} · {formatFileSizeAr(selected.size)}
              </bdi>
              <button
                type="button"
                className="btn btn-text btn-sm"
                disabled={frozen}
                data-testid={`contract-file-clear-${version.id}`}
                onClick={clearChoice}
              >
                إزالة الاختيار
              </button>
            </>
          ) : (
            <span className="tiny">لم يُختَر ملف بعد.</span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy || uploading || refreshing || !gate.canSend}
            aria-busy={uploading}
            data-testid={`contract-file-upload-${version.id}`}
            onClick={() => void upload()}
          >
            رفع المستند والتحقق منه
          </button>
          {/* The upload is create-intent → PUT (up to 25 MiB) → server-side
              fetch + SHA-256. Without this the input just went disabled and
              the only confirmation was a banner several cards above. */}
          {uploading ? (
            <span className="tiny" role="status" aria-live="polite">
              <span className="spinner" aria-hidden="true" /> جاري الرفع والتحقق من الملف على
              الخادم…
            </span>
          ) : null}
        </div>

        {fileError ? (
          <span id={helpId} className="tiny" role="alert" style={{ color: 'var(--rose-ink)' }}>
            {fileError}
          </span>
        ) : (
          <span id={helpId} className="tiny">
            PDF فقط، بحد أقصى 25 ميجابايت. يتحقق الخادم من الملف بنفسه بعد الرفع. الاستبدال ينشئ
            نسخة جديدة من الملف ولا يحذف السابقة.
          </span>
        )}

        <div
          style={{
            marginTop: 6,
            paddingTop: 10,
            borderTop: '1px solid var(--line-2)',
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy || uploading || refreshing || !canRelease}
            aria-busy={busy}
            data-testid={`contract-release-${version.id}`}
            onClick={() => void release()}
          >
            اعتماد النسخة
          </button>
          {/* Four reasons, four sentences, ordered by how far the operator
              is from being able to act on them. The unconfirmed upload is
              named FIRST because it is the only one where the sentence
              beside the button is the ONLY accurate thing in the panel;
              the pending selection next, because it is the one the operator
              can end without a network round trip; and the generic «يلزم
              اكتمال أحدث رفع» last, because on either of the first two it
              would be read as a server-side wait. */}
          {canRelease ? (
            <span className="tiny">الاعتماد إجراء منفصل: يجعل هذه النسخة هي السارية للعميل.</span>
          ) : unconfirmedUpload ? (
            <span className="tiny">
              رُفع الملف وتحقّق منه الخادم، لكن هذه الصفحة لم تُحدَّث بعد، والاعتماد الآن يجري على
              بيانات سابقة للرفع. حدّث البيانات أولاً ثم اعتمد.
            </span>
          ) : pendingSelection ? (
            <span className="tiny">
              اخترتَ ملفاً لم يُرفع بعد. الاعتماد يعتمد المستند المرفوع والمُتحقَّق منه على الخادم،
              لا الملف المعروض هنا — ارفعه أو أزل الاختيار قبل الاعتماد.
            </span>
          ) : (
            <span className="tiny">يلزم اكتمال أحدث رفع والتحقق منه قبل الاعتماد.</span>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * The chosen file's size, so «رفع» is a decision and not a leap.
 *
 * Kilobyte-and-up only: a contract PDF is never plausibly measured in
 * bytes, and the number is LTR-isolated because it sits in an RTL sentence
 * next to a filename that may be Arabic.
 */
export function formatFileSizeAr(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  const mib = bytes / (1024 * 1024);
  if (mib >= 1) return `${isolateLtr(mib.toFixed(1))} ميجابايت`;
  const kib = Math.max(1, Math.round(bytes / 1024));
  return `${isolateLtr(String(kib))} كيلوبايت`;
}

function ContractTimeline({ contract }: { readonly contract: AgencyContract }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div className="lbl" style={{ marginBottom: 8 }}>
        سجل العقد
      </div>
      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
        {contract.events.map((event, index) => (
          <li key={`${event.event_type}-${event.occurred_at}-${index}`} className="tiny">
            {CONTRACT_EVENT_LABELS_AR[event.event_type] ?? event.event_type}
            {event.version_number !== null ? ` · النسخة ${event.version_number}` : ''}
            {event.actor_name ? ` · ${event.actor_name}` : ''} ·{' '}
            <LtrRun>{formatDateTimeAr(event.occurred_at)}</LtrRun>
            {event.reason ? ` — ${event.reason}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Keyed by `ContractEventType`, not `string`, so a new wire value is a TYPE
// error rather than a raw code rendered inside an RTL list.
/**
 * Task 303-B — the receipts, on the agency's own surface. §3.5 stores them
 * because "an audit record nobody can read is not auditable", so they have
 * to be rendered somewhere.
 *
 * Who, when, and which statement version. The name is the SNAPSHOT frozen
 * at action time — the timeline resolves the same one, so a later rename
 * cannot make one action show two different names. No digest: the agency
 * already has it on the document object.
 */
function AgencyAcknowledgementList({
  acknowledgements,
}: {
  readonly acknowledgements: ReadonlyArray<AgencyContractAcknowledgement>;
}) {
  if (acknowledgements.length === 0) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <div className="lbl" style={{ marginBottom: 6 }}>
        إقرارات العميل بالاطلاع
      </div>
      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
        {acknowledgements.map((ack, index) => (
          <li key={`${ack.acknowledged_at}-${index}`} className="tiny">
            <bdi>{ack.acknowledged_by_name ?? '—'}</bdi> ·{' '}
            <LtrRun>{formatDateTimeAr(ack.acknowledged_at)}</LtrRun> · نسخة نص الإقرار:{' '}
            {isolateLtr(ack.ack_text_version)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export const CONTRACT_EVENT_LABELS_AR: Readonly<Record<ContractEventType, string>> = {
  contract_created: 'إنشاء العقد',
  version_created: 'إنشاء نسخة',
  document_attached: 'إرفاق المستند',
  version_released: 'اعتماد النسخة',
  version_superseded: 'استبدال النسخة',
  contract_archived: 'أرشفة العقد',
  contract_restored: 'إلغاء الأرشفة',
  // Task 303-B. «إقرار» — a record OF REVIEW, never «توقيع» (§2.4).
  version_acknowledged: 'إقرار العميل بالاطلاع',
};

// =====================================================================
// Client surface — §2.10 fields only
// =====================================================================

function ClientSurface({
  data,
  onDownload,
  downloadingIds,
  onAcknowledge,
  acknowledgingIds,
}: {
  readonly data: ClientContractOverviewData;
  readonly onDownload: (documentId: string, fallbackName: string) => void;
  readonly downloadingIds?: ReadonlySet<string>;
  readonly onAcknowledge: (documentId: string) => void;
  readonly acknowledgingIds?: ReadonlySet<string>;
}) {
  if (data.contracts.length === 0) {
    return (
      <EmptyState
        title="لا يوجد عقد متاح"
        description={`لم تُتَح لك بعد أي ${CONTRACT_RELEASED_NOUN_INDEF_AR} من عقد الخدمات.`}
      />
    );
  }
  return (
    <>
      {data.contracts.map((contract) => (
        <ClientContractCard
          key={contract.id}
          contract={contract}
          canDownload={data.can_download}
          onDownload={onDownload}
          downloadingIds={downloadingIds}
          onAcknowledge={onAcknowledge}
          acknowledgingIds={acknowledgingIds}
          actorProfileIncomplete={data.actor_profile_incomplete}
        />
      ))}
    </>
  );
}

function ClientContractCard({
  contract,
  canDownload,
  onDownload,
  downloadingIds,
  onAcknowledge,
  acknowledgingIds,
  actorProfileIncomplete,
}: {
  readonly contract: ClientContract;
  readonly canDownload: boolean;
  readonly onDownload: (documentId: string, fallbackName: string) => void;
  readonly downloadingIds?: ReadonlySet<string>;
  readonly onAcknowledge: (documentId: string) => void;
  readonly acknowledgingIds?: ReadonlySet<string>;
  readonly actorProfileIncomplete: boolean;
}) {
  const current = contract.versions.find((v) => v.status === 'released') ?? null;
  const history = contract.versions.filter((v) => v.status === 'superseded');

  return (
    <section className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>{contract.title}</h2>
        {contract.archived ? <span className="pill pill-archived">مؤرشف</span> : null}
      </div>
      {/* 303-A-B — the client this agreement belongs to. `/app/contracts`
          answers for EVERY client the caller is assigned to, so one list can
          span more than one, and every figure below is client-specific. */}
      <p className="tiny" style={{ marginTop: -6, marginBottom: 10 }}>
        <bdi>{contract.client_name}</bdi>
      </p>
      {/* The word alone reads as "cancelled" on your own contract, which is
          the opposite of §2.6 — an archived contract stays readable and
          downloadable forever. The agency surface explains archiving twice;
          the client got the pill and nothing else. */}
      {contract.archived ? (
        <p className="tiny" style={{ marginTop: -6, marginBottom: 10 }}>
          أرشفت الوكالة هذا العقد لديها. يبقى المستند متاحاً لك للقراءة والتنزيل كما هو.
        </p>
      ) : null}
      {current ? (
        <>
          <VersionBlock
            version={current}
            heading={CONTRACT_RELEASED_NOUN_AR}
            canDownload={canDownload}
            onDownload={onDownload}
            downloadingIds={downloadingIds}
            documentId={current.document?.id ?? null}
            documentName={current.document?.filename ?? null}
          />
          <ClientAcknowledgementBlock
            version={current}
            archived={contract.archived}
            canAcknowledge={contract.can_acknowledge}
            onAcknowledge={onAcknowledge}
            acknowledgingIds={acknowledgingIds}
            actorProfileIncomplete={actorProfileIncomplete}
          />
        </>
      ) : (
        <p className="body">لا توجد {CONTRACT_RELEASED_NOUN_INDEF_AR} حالياً.</p>
      )}

      {history.length > 0 ? (
        <div style={{ marginTop: 18 }}>
          <div className="lbl" style={{ marginBottom: 8 }}>
            النسخ السابقة
          </div>
          {history.map((version) => (
            <div key={version.id}>
              <VersionBlock
                version={version}
                heading={`النسخة ${version.version_number}`}
                canDownload={canDownload}
                onDownload={onDownload}
                downloadingIds={downloadingIds}
                documentId={version.document?.id ?? null}
                documentName={version.document?.filename ?? null}
                compact
              />
              {/* §2.5 — a receipt SURVIVES supersession. The version it
                  names cannot be acknowledged any more, but what was
                  already recorded about it stays visible here. */}
              <ClientAcknowledgementReceipt acknowledgement={version.acknowledgement} compact />
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

// =====================================================================
// Shared version rendering
// =====================================================================

/**
 * Renders ONLY the fields that exist on `ContractVersionBase` — the
 * client-safe set. Agency-only fields (`sha256`, `byte_size`, provenance)
 * are not read here even when the caller passes an `AgencyContractVersion`,
 * so this component can never widen the client surface by accident.
 */
// EXPORTED FOR RENDER TESTS. It is prop-driven and effect-free, so the
// surface the staging visual pass found the date defect on can be mounted
// directly rather than reached through the page's data loading.
export function VersionBlock({
  version,
  heading,
  canDownload,
  onDownload,
  downloadingIds,
  compact = false,
  documentId,
  documentName,
}: {
  readonly version: ContractVersionBase & {
    readonly document?: { readonly id: string; readonly filename: string } | null;
  };
  readonly heading: string;
  readonly canDownload: boolean;
  readonly onDownload: (documentId: string, fallbackName: string) => void;
  readonly downloadingIds?: ReadonlySet<string>;
  readonly compact?: boolean;
  readonly documentId?: string | null;
  readonly documentName?: string | null;
}) {
  const docId = documentId ?? version.document?.id ?? null;
  const docName = documentName ?? version.document?.filename ?? null;

  return (
    <div style={{ marginTop: compact ? 12 : 6 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 8,
        }}
      >
        {/* A real heading, not a styled span: these are the structural
            headings of each version block, and a screen-reader user
            navigating by heading was skipping straight past them. */}
        <h3 className="h-card" style={{ margin: 0 }}>
          {heading}
        </h3>
        <span className={`pill ${CONTRACT_VERSION_STATUS_PILL[version.status]}`}>
          {CONTRACT_VERSION_STATUS_LABELS_AR[version.status]}
        </span>
        <span className="tiny">النسخة {isolateLtr(String(version.version_number))}</span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        <Fact label="الباقة" value={version.package_name} />
        <Fact label="المبلغ" value={formatContractAmountAr(version.amount, version.currency)} />
        <Fact
          label="دورة الاحتساب"
          value={CONTRACT_BILLING_INTERVAL_LABELS_AR[version.billing_interval]}
        />
        <Fact
          label="السريان"
          value={formatContractPeriodAr(version.effective_from, version.effective_to)}
        />
        <Fact label="المدة" value={formatContractTermAr(version.term_months)} />
        {/* §3.7 — the client's "version history" IS these two timestamps
            plus the version number. Dropping them left a client looking at
            «النسخة 1 · مستبدَل» with no idea when it was replaced, which is
            two thirds of the only history the client is allowed to have. */}
        {/* NOT «تاريخ التوثيق». This timestamp is the moment the agency
            pressed «اعتماد النسخة» INSIDE the product; the platform does not
            know when the parties actually attested the document, and the
            pill two lines above says so. Labelling it as the attestation
            date would re-attach the act of توثيق to the platform, which is
            exactly what §1 and §2.4 forbid. */}
        {version.released_at ? (
          <Fact
            label="تاريخ إتاحة النسخة"
            value={<LtrRun>{formatContractDateAr(version.released_at)}</LtrRun>}
          />
        ) : null}
        {version.superseded_at ? (
          <Fact
            label="تاريخ الاستبدال"
            value={<LtrRun>{formatContractDateAr(version.superseded_at)}</LtrRun>}
          />
        ) : null}
      </div>

      {version.services.length > 0 ? (
        <div style={{ marginTop: 12 }}>
          <div className="lbl" style={{ marginBottom: 6 }}>
            الخدمات المشمولة
          </div>
          <ul style={{ margin: 0, paddingInlineStart: 18 }}>
            {version.services.map((line, index) => (
              <li key={`${line.name}-${index}`} className="body">
                {formatContractServiceAr(line)}
                {line.notes ? <span className="tiny"> — {line.notes}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {docId && docName ? (
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          {/* `bdi` isolates the filename: in «عقد الخدمات 2026.pdf» the dot
              sits between an AN run and an L run and resolves to the
              paragraph direction, displacing the extension. */}
          <bdi className="tiny">{docName}</bdi>
          {canDownload ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={downloadingIds?.has(docId) === true}
              aria-busy={downloadingIds?.has(docId) === true}
              // Every version's button would otherwise be announced as the
              // same «تنزيل المستند», leaving a screen-reader user with N
              // indistinguishable buttons on a multi-version card.
              aria-label={`تنزيل ${docName}`}
              onClick={() => onDownload(docId, docName)}
            >
              {downloadingIds?.has(docId) === true ? 'جاري التنزيل…' : 'تنزيل المستند'}
            </button>
          ) : (
            <span className="tiny">تنزيل المستند غير متاح لصلاحيتك.</span>
          )}
        </div>
      ) : (
        <p className="tiny" style={{ marginTop: 12, marginBottom: 0 }}>
          لم يُرفق مستند بعد.
        </p>
      )}
    </div>
  );
}

/**
 * Task 303-B — the acknowledgement block. NOT a signature panel, and no
 * string in it may suggest one (303-A §2.4).
 *
 * The statement is deictic, so Task 302 §5's conditions are conditions on
 * its TRUTH, not styling. Full derivation in `tasks/303B-…md` §2.10:
 *
 *   U1/U2'/U3'  file name, availability date and version number rendered
 *               HERE, beside the statement, not only further up the page.
 *   U4          the statement's version identifier is shown, and STAYS
 *               after acknowledging together with the statement — an
 *               identifier that resolves to no text is not a disclosure.
 *   U5          reachability is a SERVER-side gate conjunct, so
 *               `can_acknowledge` already carries it and this block needs
 *               no check of its own. Never gated on having clicked
 *               download — the platform cannot know that.
 *   U6          one always-visible block: no tooltip, no accordion. Clause
 *               6 is the limitation, and a limitation behind a disclosure
 *               was not shown.
 *
 * THE CHECKSUM IS NOT DISPLAYED, and the statement does not claim it was:
 * §2.9 Option A is the Product Owner's decision. The version number, the
 * file name and the availability date identify the document to the reader;
 * the digest stays server-side, where the receipt binds it.
 */
function ClientAcknowledgementBlock({
  version,
  archived,
  canAcknowledge,
  onAcknowledge,
  acknowledgingIds,
  actorProfileIncomplete,
}: {
  readonly version: ClientContractVersion;
  readonly archived: boolean;
  /**
   * `contract.can_acknowledge` — decided per client, which is why it is
   * read from the contract and not from the payload. `can_download` is
   * still folded across every assigned client, so gating on it here would
   * produce a false refusal on a contract the server would accept.
   */
  readonly canAcknowledge: boolean;
  readonly onAcknowledge: (documentId: string) => void;
  readonly acknowledgingIds?: ReadonlySet<string>;
  /**
   * Payload-level, and about the READER, not the contract: true when their
   * own profile carries no name and no email, so the server would have to
   * write a receipt that names nobody — and refuses.
   */
  readonly actorProfileIncomplete: boolean;
}) {
  const documentId = version.document?.id ?? null;

  // Already recorded: the receipt, with the statement it confirmed (U4).
  // No un-acknowledge control, because there is no un-acknowledge path.
  if (version.acknowledgement) {
    return (
      <section
        className="card"
        style={{ padding: 16, marginTop: 16, background: 'var(--surface-2, transparent)' }}
      >
        <div className="lbl" style={{ marginBottom: 8 }}>
          الإقرار بالاطلاع
        </div>
        <ClientAcknowledgementReceipt acknowledgement={version.acknowledgement} />
        <p className="body" style={{ marginTop: 10, marginBottom: 0 }}>
          {CONTRACT_ACK_STATEMENT_AR}
        </p>
      </section>
    );
  }

  // Nothing to acknowledge yet: this version has no complete document, or
  // it is not the released one. Nothing to say, so nothing is said.
  if (!version.acknowledgeable || !documentId) {
    return null;
  }

  // ENTITLED TO SEE IT, NOT TO DO IT — said, not rendered as silence, and
  // matching what this page already does for the analogous download case.
  // Deliberately neutral about WHY: 303-A-B forbids naming the agency's
  // commercial standing, and a pinned test asserts that vocabulary is
  // absent from the whole page.
  // An archived contract is an agency decision this page explains above,
  // so attributing it to «هذا الحساب» would name the wrong cause.
  if (archived) {
    return (
      <p className="tiny" style={{ marginTop: 16, marginBottom: 0 }}>
        لا يقبل العقد المؤرشف إقراراً جديداً.
      </p>
    );
  }

  if (!canAcknowledge) {
    // ACTIONABLE WHERE IT CAN BE. «غير متاح» is correct, and useless to a
    // reader whose blocker is that their own account carries no name and
    // no email — the one refusal reason someone can actually clear. It
    // names the REQUIREMENT rather than promising the OUTCOME, because
    // commercial standing can block as well and this page may not say so
    // (303-A-B).
    //
    // It points at the agency rather than at a settings screen because
    // there is no self-service one: `full_name` is writable only through
    // `accept-invitation` and `signup-agency`, and `SettingsPage` renders
    // it read-only. "Edit your profile" would be a dead end dressed as
    // help.
    //
    // It asks for a name or an email and shows neither, so nothing here
    // discloses a login phone or an internal identifier; the flag behind
    // it is a bare boolean about the reader themselves.
    if (actorProfileIncomplete) {
      return (
        <p className="tiny" style={{ marginTop: 16, marginBottom: 0 }}>
          يسجل الإقرار بالاطلاع باسم صاحبه، ولا يحمل حسابك حالياً اسماً ولا بريداً إلكترونياً. تواصل
          مع الوكالة لاستكمال اسم حسابك أو بريده الإلكتروني، ثم عد إلى هذه الصفحة.
        </p>
      );
    }
    return (
      <p className="tiny" style={{ marginTop: 16, marginBottom: 0 }}>
        الإقرار بالاطلاع غير متاح حالياً على هذا الحساب.
      </p>
    );
  }

  const busy = acknowledgingIds?.has(documentId) === true;

  return (
    <section
      className="card"
      style={{ padding: 16, marginTop: 16, background: 'var(--surface-2, transparent)' }}
    >
      <div className="lbl" style={{ marginBottom: 8 }}>
        الإقرار بالاطلاع
      </div>

      {/* U1 / U2' / U3' — the three facts the statement names, beside it. */}
      <p className="tiny" style={{ marginTop: 0, marginBottom: 10 }}>
        النسخة {isolateLtr(String(version.version_number))} ·{' '}
        <bdi>{version.document?.filename ?? '—'}</bdi> · أتيح بتاريخ{' '}
        <LtrRun>{formatContractDateAr(version.released_at)}</LtrRun>
      </p>

      {/* U6 — ONE block, always visible, never behind a disclosure. */}
      <p className="body" style={{ marginTop: 0, marginBottom: 10 }}>
        {CONTRACT_ACK_STATEMENT_AR}
      </p>

      {/* U4 — which text was confirmed. The same string the record stores. */}
      <p className="tiny" style={{ marginTop: 0, marginBottom: 10 }}>
        نسخة نص الإقرار: {isolateLtr(CONTRACT_ACK_TEXT_VERSION)}
      </p>

      {/* THE PRODUCT FACT, BEFORE THE CLICK — not a legal one. The row is
          append-only for every role including `service_role` and there is
          no revocation path, so one click writes something permanent and
          the reader should know. Calling it "irrevocable" would assert a
          LEGAL property the product has not decided, which Group H bans.
          The second sentence names what the agency CAN do — release a
          replacement — because it cannot remove this. */}
      <p className="tiny" style={{ marginTop: 0, marginBottom: 12 }}>
        لا يوجد إجراء داخل المنصة لإزالة هذا الإقرار بعد تسجيله. إن سجّلته بالخطأ، تواصل مع الوكالة
        التي تدير حسابك لإتاحة نسخة جديدة من العقد.
      </p>

      <button
        type="button"
        className="btn btn-primary btn-sm"
        disabled={busy}
        aria-busy={busy}
        onClick={() => onAcknowledge(documentId)}
      >
        {busy ? 'جاري التسجيل…' : 'أقر بالاطلاع على هذا المستند'}
      </button>
    </section>
  );
}

/**
 * The receipt line. Two facts, both the reader's own: when they
 * acknowledged, and which statement version they confirmed. No digest, no
 * identifiers, and never another user's receipt — the SQL behind it is
 * pinned to `auth.uid()`.
 *
 * The TIME and the ZONE are shown, not just the date: a bare local date
 * over a UTC timestamp makes the screenshot and the export name different
 * days for the same row.
 */
// EXPORTED FOR RENDER TESTS, for the same reason as {@link VersionBlock}:
// this is where the acknowledged-at timestamp renders.
export function ClientAcknowledgementReceipt({
  acknowledgement,
  compact = false,
}: {
  readonly acknowledgement: ClientContractAcknowledgement | null;
  readonly compact?: boolean;
}) {
  if (!acknowledgement) return null;
  // NO STALE-TEXT BRANCH. `app.agency_client_contract_my_acknowledgement`
  // filters to the CURRENT statement version, so a receipt reaching this
  // component can only ever carry `CONTRACT_ACK_TEXT_VERSION`. A branch for
  // the other case would be unreachable code — and its test would be three
  // source-string assertions rendering nothing, which is the vacuity this
  // task keeps finding. After an amendment the reader simply sees no
  // receipt and is asked to confirm the new text, which is the designed
  // behaviour and the one the pack asserts.
  return (
    <p className="tiny" style={{ marginTop: compact ? 6 : 0, marginBottom: 0 }}>
      سُجِّل إقرارك بالاطلاع على هذا المستند بتاريخ{' '}
      <LtrRun>{formatAcknowledgedAtAr(acknowledgement.acknowledged_at)}</LtrRun> · نسخة نص الإقرار:{' '}
      {isolateLtr(acknowledgement.ack_text_version)}
    </p>
  );
}

/**
 * The acknowledgement timestamp, with its time AND its zone. Separate from
 * `formatDateTimeAr`: there a zone label on every row is noise; here it is
 * the point, because this is the moment the record fixes.
 */
export function formatAcknowledgedAtAr(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return stripBidiControls(iso);

  // THE OFFSET IS BUILT BY HAND, IN ASCII, for two reasons. ECMA-402
  // forbids combining `dateStyle`/`timeStyle` with `timeZoneName` — a
  // spec-mandated TypeError, so that combination silently renders from the
  // catch. And `timeZoneName` emits Arabic («غرينتش+٣»), which reorders
  // inside the LTR run the rest of the stamp needs.
  const offsetMinutes = -parsed.getTimezoneOffset();
  const sign = offsetMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  const zone = `UTC${sign}${hh}:${mm}`;

  try {
    const stamp = new Intl.DateTimeFormat('ar-SA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      numberingSystem: 'latn',
      calendar: 'gregory',
    }).format(parsed);
    // `stamp` carries U+200F between its date parts — see
    // {@link stripBidiControls}. Stripped here rather than isolated, and
    // rendered inside {@link LtrRun}.
    return `${stripBidiControls(stamp)} ${zone}`;
  } catch {
    return `${parsed.toISOString().slice(0, 16).replace('T', ' ')} UTC+00:00`;
  }
}

// `value` is a NODE, not a string, so a fact whose value is a pure Latin
// run can pass `<LtrRun>` and carry its own direction. Arabic-bearing facts
// keep passing plain strings and inherit the page's RTL base, which is what
// they need — a blanket `dir="ltr"` on every fact would misorder those.
function Fact({ label, value }: { readonly label: string; readonly value: ReactNode }) {
  return (
    <div>
      <div className="lbl" style={{ marginBottom: 4 }}>
        {label}
      </div>
      <div className="body" style={{ color: 'var(--ink)' }}>
        {value}
      </div>
    </div>
  );
}

// =====================================================================
// Snapshot form
// =====================================================================

interface SnapshotFormPayload {
  readonly title?: string;
  readonly package_source: 'manual';
  readonly package_name: string;
  readonly services: ReadonlyArray<ContractServiceLine>;
  readonly amount: number;
  readonly currency: string;
  readonly billing_interval: ContractBillingInterval;
  readonly effective_from: string;
  readonly effective_to: string | null;
  readonly term_months: number | null;
}

/**
 * V1 records a MANUAL snapshot. Deriving one from an existing package
 * assignment is a real convenience, but it is a separate decision about
 * which package fields map onto which contract fields, and guessing it
 * here would freeze a wrong mapping into contract history (§2.5). The
 * wire contract already carries `package_source` / provenance so that
 * follow-up needs no schema change.
 */
export function parseServicesInput(raw: string): ContractServiceLine[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const parts = line.split('|').map((part) => part.trim());
      if (parts.length > 3) {
        throw new Error('صيغة الخدمة تقبل الاسم والكمية والوحدة فقط.');
      }
      const [namePart = '', quantityPart = '', unitPart = ''] = parts;
      if (namePart.length === 0) {
        throw new Error('اسم الخدمة مطلوب في كل سطر.');
      }
      const quantity = quantityPart.length > 0 ? Number(quantityPart) : null;
      if (quantity !== null && (!Number.isFinite(quantity) || quantity <= 0)) {
        throw new Error('كمية الخدمة يجب أن تكون رقماً موجباً، أو تُترك فارغة.');
      }
      return {
        name: namePart,
        ...(quantity !== null ? { quantity } : {}),
        ...(unitPart ? { unit: unitPart } : {}),
      };
    });
}

function ContractSnapshotForm({
  busy,
  submitLabel,
  withTitle = false,
  onSubmit,
}: {
  readonly busy: boolean;
  readonly submitLabel: string;
  readonly withTitle?: boolean;
  readonly onSubmit: (payload: SnapshotFormPayload) => Promise<void> | void;
}) {
  const [title, setTitle] = useState('');
  const [packageName, setPackageName] = useState('');
  const [services, setServices] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('SAR');
  const [interval, setInterval] = useState<ContractBillingInterval>('monthly');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [term, setTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Two of these forms can be open at once (create + replacement), so the
  // control ids must be unique per instance or `htmlFor` binds to whichever
  // rendered first.
  const idPrefix = useId();

  const parsedServices = useMemo(() => {
    try {
      return { value: parseServicesInput(services), error: null } as const;
    } catch (err) {
      return {
        value: [] as ContractServiceLine[],
        error: err instanceof Error ? err.message : 'تحقق من صيغة الخدمات.',
      } as const;
    }
  }, [services]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const amountValue = parseContractAmountInput(amount);
    if (withTitle && title.trim().length === 0) {
      setError('اسم العقد مطلوب.');
      return;
    }
    if (packageName.trim().length === 0) {
      setError('اسم الباقة مطلوب.');
      return;
    }
    if (parsedServices.error) {
      setError(parsedServices.error);
      return;
    }
    if (parsedServices.value.length === 0) {
      setError('أدخل خدمة واحدة على الأقل.');
      return;
    }
    if (amount.trim().length === 0) {
      setError('المبلغ مطلوب.');
      return;
    }
    if (amountValue === null) {
      setError('المبلغ يجب أن يكون رقماً غير سالب وبمنزلتين عشريتين كحد أقصى.');
      return;
    }
    if (!/^[A-Za-z]{3}$/.test(currency.trim())) {
      setError('رمز العملة يجب أن يكون ثلاثة أحرف.');
      return;
    }
    if (from.trim().length === 0) {
      setError('تاريخ بداية السريان مطلوب.');
      return;
    }
    if (to.trim().length > 0 && to < from) {
      setError('تاريخ نهاية السريان يسبق البداية.');
      return;
    }
    const termValue = term.trim().length > 0 ? Number(term) : null;
    if (termValue !== null && (!Number.isInteger(termValue) || termValue <= 0)) {
      setError('المدة بالأشهر يجب أن تكون رقماً صحيحاً موجباً.');
      return;
    }
    setError(null);
    void onSubmit({
      ...(withTitle ? { title: title.trim() } : {}),
      package_source: 'manual',
      package_name: packageName.trim(),
      services: parsedServices.value,
      amount: amountValue,
      currency: currency.trim().toUpperCase(),
      billing_interval: interval,
      effective_from: from,
      effective_to: to.trim().length > 0 ? to : null,
      term_months: termValue,
    });
  };

  return (
    <form className="stack-sm" onSubmit={submit} style={{ marginTop: 12 }}>
      {withTitle ? (
        <div className="field">
          <label htmlFor={`${idPrefix}-title`}>اسم العقد</label>
          <input
            id={`${idPrefix}-title`}
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
      ) : null}
      <div className="field">
        <label htmlFor={`${idPrefix}-package`}>اسم الباقة كما في العقد</label>
        <input
          id={`${idPrefix}-package`}
          value={packageName}
          maxLength={200}
          onChange={(e) => setPackageName(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor={`${idPrefix}-services`}>الخدمات — سطر لكل خدمة</label>
        <textarea
          id={`${idPrefix}-services`}
          rows={4}
          value={services}
          onChange={(e) => setServices(e.target.value)}
          placeholder={'إدارة الحسابات | 1 | شهرياً\nتصاميم | 12'}
        />
        <span className="tiny">الصيغة: الاسم | الكمية | الوحدة. الكمية والوحدة اختياريتان.</span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}
      >
        <div className="field">
          <label htmlFor={`${idPrefix}-amount`}>المبلغ</label>
          <input
            id={`${idPrefix}-amount`}
            inputMode="decimal"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-currency`}>العملة (ISO)</label>
          <input
            id={`${idPrefix}-currency`}
            value={currency}
            maxLength={3}
            onChange={(e) => setCurrency(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-interval`}>دورة الاحتساب</label>
          <select
            id={`${idPrefix}-interval`}
            value={interval}
            onChange={(e) => setInterval(e.target.value as ContractBillingInterval)}
            style={{
              width: '100%',
              padding: '9px 10px',
              borderRadius: 'var(--r-sm)',
              border: '1px solid var(--line-2)',
              background: 'var(--paper)',
              color: 'var(--ink)',
              fontSize: 13,
            }}
          >
            {(Object.keys(CONTRACT_BILLING_INTERVAL_LABELS_AR) as ContractBillingInterval[]).map(
              (key) => (
                <option key={key} value={key}>
                  {CONTRACT_BILLING_INTERVAL_LABELS_AR[key]}
                </option>
              ),
            )}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-from`}>بداية السريان</label>
          <input
            id={`${idPrefix}-from`}
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-to`}>نهاية السريان (اختياري)</label>
          <input
            id={`${idPrefix}-to`}
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-term`}>المدة بالأشهر (اختياري)</label>
          <input
            id={`${idPrefix}-term`}
            inputMode="numeric"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>
      </div>
      {error ? (
        <span className="tiny" role="alert" style={{ color: 'var(--rose-ink)' }}>
          {error}
        </span>
      ) : null}
      <div>
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy} aria-busy={busy}>
          {submitLabel}
        </button>
      </div>
      <span className="tiny">
        تُحفظ هذه البيانات كلقطة ثابتة لحظة الاتفاق. تعديل الباقات لاحقاً لا يغيّر أي نسخة محفوظة.
      </span>
    </form>
  );
}

// =====================================================================

/**
 * A timeline timestamp, in the READER'S timezone.
 *
 * The first version printed `toISOString()` — UTC, unlabelled — on the
 * page's only audit trail. In Riyadh (+3) an archive performed at 01:00
 * local was logged on screen as 22:00 the previous day. It also sat after
 * Arabic text unisolated, so its own components were reordered. Both are
 * fixed: local formatting, and the run's direction carried by {@link
 * LtrRun} at the render site.
 *
 * The isolate that used to wrap this string was not enough — `ar-SA` embeds
 * U+200F inside its own pattern, so the digits were reordered INSIDE it.
 * See {@link stripBidiControls}.
 */
export function formatDateTimeAr(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return stripBidiControls(iso);
  try {
    return stripBidiControls(
      new Intl.DateTimeFormat('ar-SA', {
        dateStyle: 'short',
        timeStyle: 'short',
        numberingSystem: 'latn',
        calendar: 'gregory',
      }).format(parsed),
    );
  } catch {
    return parsed.toISOString().slice(0, 16).replace('T', ' ');
  }
}

/**
 * Wire codes never reach the reader. Each branch is a sentence about what
 * happened and what to do, in the product's own language.
 */
export function describeContractError(err: unknown): string {
  if (isApiError(err)) {
    switch (err.code) {
      // The RPCs raise 42501 for archived-contract, archived-client,
      // wrong-state, incomplete-upload and the upload-attempt cap as well as
      // for genuine lack of authority, and `permission_denied` is forbidden
      // from carrying detail. Telling an owner who lost a release race that
      // they lack authority they in fact hold is a false diagnosis that
      // sends them to the wrong person, so the copy names both possibilities
      // instead of guessing between them.
      case 'permission_denied':
        return 'تعذّر تنفيذ هذا الإجراء: إما أن حالة العقد لا تسمح به الآن، أو أن صلاحيتك لا تشمله.';
      case 'not_found':
        return 'العقد أو المستند غير متاح.';
      // The download endpoint answers 409 when the STORED object fails
      // verification — wrong digest, wrong size, no longer a PDF. Retrying
      // that can never succeed, so the copy must not ask for a retry; it is
      // the same "instruction that can never work" the load path was fixed
      // for. The upload conflict genuinely is retryable, so both are named.
      case 'conflict':
        return err.body.detail === 'contract_document_unverifiable'
          ? 'تعذّر التحقق من الملف المحفوظ لهذه النسخة. لا تُعِد المحاولة — تواصل مع الوكالة لإعادة رفع المستند.'
          : 'تعذر إتمام العملية: المستند تغيّر أو لم يصل بعد. أعد المحاولة.';
      case 'validation_error':
        return 'تحقق من الحقول المدخلة ثم أعد المحاولة.';
      case 'commercial_activation_required':
        return 'الحساب غير مفعّل تجارياً حالياً.';
      default:
        return 'تعذر إتمام العملية. حاول مرة أخرى.';
    }
  }
  if (err instanceof Error && err.message === 'upload_failed') {
    return 'فشل رفع الملف إلى التخزين. أعد المحاولة.';
  }
  return 'تعذر إتمام العملية. حاول مرة أخرى.';
}
